<template>
  <div class="stb-root" :class="{ 'stb-root--dragging': !!draggingFrom }">
    <!-- rail-always: the erm board shows a rail in classic too, and this page
         has no other title -->
    <FiPageRail title="Ticket Board" :chip="contextTenantName" rail-always>
      <template #actions>
        <b-button size="sm" variant="primary" title="Create a new support ticket" aria-label="Add ticket"
                  @click="$refs.createModal.open()">
          <i class="fa fa-plus"></i><span class="stb-btn-label">&nbsp;Add Ticket</span>
        </b-button>
        <!-- list | board: the same switch the list carries -->
        <support-view-toggle active="board" />
        <b-button v-if="isAdminView" size="sm" variant="outline-secondary" title="Monthly billable hours"
                  aria-label="Monthly billable hours" @click="openBillableSummary()">
          <i class="fa fa-usd"></i>
        </b-button>
        <b-button size="sm" variant="outline-secondary" title="About this board" aria-label="Help"
                  @click="$bvModal.show('stb-help-modal')">
          <i class="fa fa-info-circle"></i>
        </b-button>
        <b-button size="sm" variant="outline-secondary" :disabled="loading"
                  title="Reload the board" aria-label="Refresh" @click="reload()">
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
        </b-button>
      </template>
    </FiPageRail>

    <b-modal id="stb-help-modal" title="About the Ticket Board" size="lg" ok-only ok-title="Close">
      <p>Every support ticket the Support Tickets list would show you, laid out as one lane per status.
        Click a card to open the ticket.</p>
      <template>
        <p><b>Drag a card to another lane</b> to change its status. The move is silent for the customer
          unless <i>Email customer on status change</i> is ticked, in which case it sends the same
          branded status email as Save on the ticket page.</p>
        <p><b>Draft</b> holds tickets staff have written but not sent; the billing audits open theirs
          here. Nothing drags in or out of it: open the card and press <i>Send to customer</i>, which emails
          the customer and moves the ticket to Waiting On Customer. Customers never see a draft.</p>
        <p><b>Click the photo at the bottom of a card</b> to reassign it: the roster
          opens over the board and one click hands the ticket over. This is silent for the
          customer whatever the <i>Email customers</i> box says — assignment is internal
          triage — and it posts to Slack the way it does from the ticket page. The card keeps
          its lane and its place in it. Unlike dragging, this works on a phone.</p>
        <p><b>Drag a card up or down within a lane</b> to rank it. The order is saved and shared by
          everyone on the board, so the top of a lane is what gets worked next. Tickets nobody has
          ranked yet sit below the ranked ones, highest urgency first; the moment you drag one, the
          whole lane takes its ranks from the order you see.</p>
        <p>Completed and Cancelled lanes are not ranked and show only the last 7 days of closures.
          The Status chips (All, Open, Active) are fold presets: each opens its lanes and folds the rest
          to strips. A strip still takes a drop — hold a card over it and it opens under the cursor — so a
          card can be completed or cancelled without opening the lane first. Any lane can be folded or
          opened with the <i class="fa fa-angle-double-left"></i> in its header, and the board remembers.</p>
        <p class="stb-small">Dragging is off on phones and tablets; open the ticket to change its status there.</p>
      </template>

    </b-modal>

    <!-- invoice prep (staff): one month's billable dev hours per customer,
         rendered server-side in the paste-ready "#1234 - subject - 4 hrs"
         shape. opens on the month just ended; picking another month
         regenerates. -->
    <b-modal id="stb-billable-modal" title="Monthly Billable Hours" size="lg" ok-only ok-title="Close">
      <div class="stb-billable-bar">
        <b-form-select v-model="billableMonth" :options="billableMonthOptions" size="sm"
                       class="stb-billable-month" aria-label="Month" @change="loadBillableSummary()" />
        <b-form-select v-model="billableAssignee" :options="billableAssigneeOptions" size="sm"
                       class="stb-billable-assignee" aria-label="Assigned to" @change="loadBillableSummary()" />
        <b-button size="sm" variant="outline-secondary" :disabled="billableLoading"
                  title="Regenerate" aria-label="Regenerate" @click="loadBillableSummary()">
          <i class="fa fa-refresh" :class="{ 'fa-spin': billableLoading }"></i>
        </b-button>
        <b-button size="sm" variant="primary" class="ml-auto" :disabled="billableLoading || !billableText"
                  title="Copy to clipboard" aria-label="Copy" @click="copyBillableSummary()">
          <i class="fa" :class="billableCopied ? 'fa-check' : 'fa-clipboard'" aria-hidden="true"></i>&nbsp;{{ billableCopied ? 'Copied' : 'Copy' }}
        </b-button>
      </div>
      <div v-if="billableError" class="text-danger stb-small">{{ billableError }}</div>
      <template v-else-if="billableSummary">
        <div v-if="billableMissingHours" class="stb-billable-warn">
          <i class="fa fa-exclamation-triangle" aria-hidden="true"></i> No hours entered: {{ billableMissingHours }}
        </div>
        <pre v-if="billableText" class="stb-billable-text">{{ billableText }}</pre>
        <p v-else class="stb-small stb-billable-empty">No billable tickets completed in {{ billableMonthLabel }}{{ billableAssigneeLabel }}.</p>
      </template>
    </b-modal>

    <!-- the ticket, in place (shared with the list). on close the board
         refetches that one ticket and patches its card. -->
    <!-- "Add Ticket" opens the create form over the board; the new card
         lands in New (a ticket whose attachments failed opens for the retry) -->
    <support-ticket-create-modal ref="createModal" modal-id="stb-ticket-create-modal"
                                 @created="onTicketCreated"
                                 @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />
    <support-ticket-modal ref="ticketModal" modal-id="stb-ticket-modal"
                          @hidden="onTicketModalHidden"
                          @showAlert="(msg, variant, ms) => $emit('showAlert', msg, variant, ms)" />

    <div v-if="loading && !tickets.length && !error" class="text-center" style="margin: 50px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>
    <div v-else-if="error" class="text-center text-danger" style="margin: 50px;">
      <p>{{ error }}</p>
      <b-button size="sm" variant="outline-secondary" @click="reload()">Retry</b-button>
    </div>

    <div v-else :class="{ 'stb-stale': loading, 'stb-filters-collapsed': !filtersExpanded }">
      <!-- phone-only filters toggle, mirroring the other boards: everything
           folds away so a phone opens straight to the lanes. -->
      <button
        type="button"
        class="stb-filter-toggle stb-mobile-only"
        :aria-expanded="filtersExpanded ? 'true' : 'false'"
        @click="filtersExpanded = !filtersExpanded"
      >
        <i class="fa fa-sliders" aria-hidden="true"></i>
        <span>Filters</span>
        <span v-if="activeFilterCount" class="stb-filter-badge">{{ activeFilterCount }}</span>
        <i class="fa" :class="filtersExpanded ? 'fa-angle-up' : 'fa-angle-down'" aria-hidden="true"></i>
      </button>

      <!-- first row: whose tickets. the customer picker and the assignee
           scope are staff controls -- a customer's list is already scoped to
           their own account, and assignment is Initek's. -->
      <div class="stb-scope-row">
        <template v-if="isAdminView">
          <button type="button" :class="['stb-chip', { 'stb-chip--active': assigneeFilter === MINE }]"
                  v-show="assigneeCounts[MINE] || assigneeFilter === MINE"
                  :aria-pressed="assigneeFilter === MINE ? 'true' : 'false'"
                  @click="assigneeFilter = MINE">Mine <span class="stb-chip-count">{{ assigneeCounts[MINE] || 0 }}</span></button>
          <button type="button" :class="['stb-chip', { 'stb-chip--active': assigneeFilter === MY_CREATED }]"
                  v-show="assigneeCounts[MY_CREATED] || assigneeFilter === MY_CREATED"
                  :aria-pressed="assigneeFilter === MY_CREATED ? 'true' : 'false'"
                  @click="assigneeFilter = MY_CREATED">My Created <span class="stb-chip-count">{{ assigneeCounts[MY_CREATED] || 0 }}</span></button>
          <button type="button" :class="['stb-chip', { 'stb-chip--active': assigneeFilter === '' }]"
                  :aria-pressed="assigneeFilter === '' ? 'true' : 'false'"
                  @click="assigneeFilter = ''">Everyone <span class="stb-chip-count">{{ assigneeCounts[''] || 0 }}</span></button>
          <!-- the list's team strip: one photo per Initek member plus the
               struck-out head for nobody. click narrows to that person's
               cards (assigned to them or opened by them); click again to go
               back to everyone. -->
          <span class="stb-team-chips" role="group" aria-label="Filter by person">
            <button type="button" class="stb-team-chip"
                    v-show="assigneeCounts[UNASSIGNED] || assigneeFilter === UNASSIGNED"
                    :class="{ 'stb-team-chip--selected': assigneeFilter === UNASSIGNED }"
                    :aria-pressed="assigneeFilter === UNASSIGNED ? 'true' : 'false'"
                    :title="`Unassigned — ${assigneeCounts[UNASSIGNED] || 0} on the board`"
                    :aria-label="`Unassigned — ${assigneeCounts[UNASSIGNED] || 0} on the board`"
                    @click="toggleAssignee(UNASSIGNED)">
              <span class="stb-team-chip-icon stb-team-chip-icon--unassigned" aria-hidden="true">
                <i class="fa fa-user"></i>
              </span>
              <span class="stb-team-chip-count" aria-hidden="true">{{ assigneeCounts[UNASSIGNED] || 0 }}</span>
            </button>
            <button v-for="member in teamChips" :key="member.user" type="button" class="stb-team-chip"
                    v-show="assigneeCounts[member.username] || isTeamChipSelected(member)"
                    :class="{ 'stb-team-chip--selected': isTeamChipSelected(member) }"
                    :aria-pressed="isTeamChipSelected(member) ? 'true' : 'false'"
                    :title="`${member.name} — ${assigneeCounts[member.username] || 0} on the board`"
                    :aria-label="`${member.name} — ${assigneeCounts[member.username] || 0} on the board`"
                    @click="toggleAssignee(member.username)">
              <img :src="member.image" :alt="member.name" class="stb-team-chip-img" />
              <span class="stb-team-chip-count" aria-hidden="true">{{ assigneeCounts[member.username] || 0 }}</span>
            </button>
          </span>
        </template>
        <!-- All / Open / Active are fold presets, not filters: each opens
             its lanes and folds the rest to strips, and from there any lane
             folds or opens on its own. the chip shows the last preset. -->
        <span class="stb-group" role="group" aria-label="Status">
          <span class="stb-group-label">Status</span>
          <button v-for="opt in STATUS_FILTERS" :key="'s-' + opt.value" type="button"
                  v-show="statusCounts[opt.value] || statusFilter === opt.value"
                  :class="['stb-chip', { 'stb-chip--active': statusFilter === opt.value }]"
                  :aria-pressed="statusFilter === opt.value ? 'true' : 'false'"
                  :title="opt.title"
                  @click="applyStatusPreset(opt.value)">
            {{ opt.text }}<span class="stb-chip-count">{{ statusCounts[opt.value] || 0 }}</span>
          </button>
        </span>
        <span v-if="isAdminView" class="stb-group" role="group" aria-label="Urgency">
          <span class="stb-group-label">Urgency</span>
          <button v-for="opt in URGENCY_FILTERS" :key="'u-' + opt.value" type="button"
                  v-show="urgencyCounts[opt.value] || urgencyFilters.includes(opt.value)"
                  :class="['stb-chip', 'stb-chip--tone', opt.variant, { 'stb-chip--active': urgencyFilters.includes(opt.value) }]"
                  :aria-pressed="urgencyFilters.includes(opt.value) ? 'true' : 'false'"
                  @click="toggleFilter('urgencyFilters', opt.value)">
            <i class="fa" :class="opt.icon" aria-hidden="true"></i>{{ opt.text }}
            <span class="stb-chip-count">{{ urgencyCounts[opt.value] || 0 }}</span>
          </button>
        </span>
        <!-- the two flags, as toned chips like every other filter: gold
             for VIP (the list's account-name treatment), green for
             billable (the card's flag), then the navbar's customer in the
             ops blue. always shown -- chips that vanish at zero read as
             missing rather than as "none". -->
        <span class="stb-group" role="group" aria-label="Flags and customer">
          <button type="button"
                  :class="['stb-chip', 'stb-chip--tone', 'stb-flag-vip', { 'stb-chip--active': vipOnly }]"
                  :aria-pressed="vipOnly ? 'true' : 'false'" title="Only tickets for VIP accounts"
                  @click="vipOnly = !vipOnly">
            <i class="fa fa-star" aria-hidden="true"></i>VIP<span class="stb-chip-count">{{ flagCounts.vip || 0 }}</span>
          </button>
          <button type="button"
                  :class="['stb-chip', 'stb-chip--tone', 'stb-flag-billable', { 'stb-chip--active': billableOnly }]"
                  :aria-pressed="billableOnly ? 'true' : 'false'" title="Only tickets marked billable"
                  @click="billableOnly = !billableOnly">
            <i class="fa fa-usd" aria-hidden="true"></i>Billable<span class="stb-chip-count">{{ flagCounts.billable || 0 }}</span>
          </button>
          <!-- the customer the navbar switcher is on, one press away. it is
               the Customer picker's own dimension rather than a filter of
               its own: pressing it fills the picker, pressing it again
               empties it, so the two can never disagree. -->
          <button v-if="currentTenant" type="button"
                  :class="['stb-chip', 'stb-chip--tone', 'stb-flag-current', { 'stb-chip--active': currentTenantOnly }]"
                  :aria-pressed="currentTenantOnly ? 'true' : 'false'"
                  :title="`Only tickets for ${currentTenantName} — the customer you are switched to`"
                  :aria-label="`Only tickets for ${currentTenantName}`"
                  @click="toggleCurrentTenant()">
            <i class="fa fa-building-o" aria-hidden="true"></i>Current<span class="stb-chip-count">{{ flagCounts.current || 0 }}</span>
          </button>
        </span>
        <div class="stb-search ml-auto">
          <i class="fa fa-search stb-search-icon" aria-hidden="true"></i>
          <input v-model="search" type="text" class="stb-search-input"
                 placeholder="Search tickets or #1234" aria-label="Search tickets">
          <button v-if="search" type="button" class="stb-search-clear"
                  aria-label="Clear search" title="Clear search" @click="search = ''">
            <i class="fa fa-times" aria-hidden="true"></i>
          </button>
        </div>
      </div>

      <!-- second row: what the cards are. the same Type / Feature / Urgency
           vocabulary as the list's dropdowns, as independent toggles. no
           Status group -- the lanes ARE the status. -->
      <div class="stb-filter-row">
        <span class="stb-group" role="group" aria-label="Type">
          <span class="stb-group-label">Type</span>
          <!-- each chip wears its tag's colour, so the filter reads as the
               cards do, and says how many cards it would leave -->
          <button v-for="opt in TYPE_FILTERS" :key="'t-' + opt.value" type="button"
                  v-show="typeCounts[opt.value] || typeFilters.includes(opt.value)"
                  :class="['stb-chip', 'stb-chip--tone', opt.variant, { 'stb-chip--active': typeFilters.includes(opt.value) }]"
                  :aria-pressed="typeFilters.includes(opt.value) ? 'true' : 'false'"
                  @click="toggleFilter('typeFilters', opt.value)">
            <i class="fa" :class="opt.icon" aria-hidden="true"></i>{{ opt.text }}
            <span class="stb-chip-count">{{ typeCounts[opt.value] || 0 }}</span>
          </button>
        </span>
        <span class="stb-group" role="group" aria-label="Feature">
          <span class="stb-group-label">Feature</span>
          <!-- the first three (plus any pressed) show; the rest fold behind
               a +N chip so the row stays short -->
          <button v-for="opt in shownFeatureFilters" :key="'f-' + opt.value" type="button"
                  :class="['stb-chip', 'stb-chip--tone', 'stb-feat-' + opt.value, { 'stb-chip--active': categoryFilters.includes(opt.value) }]"
                  :aria-pressed="categoryFilters.includes(opt.value) ? 'true' : 'false'"
                  @click="toggleFilter('categoryFilters', opt.value)">
            {{ opt.text }}<span class="stb-chip-count">{{ featureCounts[opt.value] || 0 }}</span>
          </button>
          <button v-if="hiddenFeatureCount || featuresExpanded" type="button" class="stb-chip stb-chip--more"
                  :aria-expanded="featuresExpanded ? 'true' : 'false'"
                  :title="featuresExpanded ? 'Show fewer features' : 'Show every feature'"
                  @click="featuresExpanded = !featuresExpanded">
            <template v-if="featuresExpanded"><i class="fa fa-angle-left" aria-hidden="true"></i>less</template>
            <template v-else>+{{ hiddenFeatureCount }} more</template>
          </button>
        </span>
        <template v-if="isAdminView">
          <!-- the two pickers ride the row's far right -->
          <span class="stb-scope-selects">
            <b-form-select v-model="selectedTenant" :options="tenantOptions" size="sm"
                           class="stb-tenant-select" aria-label="Customer" />
            <b-form-select v-model="createdByFilter" :options="createdByOptions" size="sm"
                           class="stb-scope-select" aria-label="Filter by creator" />
          </span>
          <!-- a drop is silent for the customer by default, like an inline
               edit on the list; this opts a session into the details page's
               status email. deliberately not remembered between visits. -->
          <b-form-checkbox v-model="notifyOnMove" size="sm" class="stb-check"
                           title="Send the customer the status-change email when a card is dropped in a new lane">
            Email customers
          </b-form-checkbox>
        </template>
      </div>

      <!-- what is narrowing the board right now, spelled out: one pill per
           active filter, each removable, plus clear-all. the chips above
           show state too, but a pressed chip among thirty unpressed ones
           is easy to miss -->
      <div v-if="activeFilterPills.length" class="stb-active">
        <span class="stb-active-label"><i class="fa fa-filter" aria-hidden="true"></i> Filtering by</span>
        <button v-for="pill in activeFilterPills" :key="pill.key" type="button" class="stb-active-pill"
                :title="'Remove ' + pill.dim + ' filter'" @click="removeFilterPill(pill)">
          <span class="stb-active-dim">{{ pill.dim }}</span> {{ pill.text }}
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
        <button type="button" class="stb-link-btn stb-active-clear" @click="clearFilters(true)">Clear all</button>
      </div>

      <div v-if="isFiltered && !matchCount" class="stb-no-matches">
        No tickets match these filters.
        <button type="button" class="stb-link-btn" @click="clearFilters">Clear filters</button>
      </div>

      <div class="stb-board" ref="board" :class="{ 'stb-board--dense': denseLanes }">
        <!-- one element per lane whether open or folded: the fold is a
             class, so the draggable inside is the same container in both
             states and sortablejs never loses the card it is carrying when
             a strip opens under it mid-drag. -->
        <div v-for="col in columnDefs" :key="col.status"
             class="stb-col" :class="['stb-col--' + col.status, dropStateClass(col.status), { 'stb-col--collapsed': isStrip(col.status) }]"
             :data-status="col.status"
             :role="isStrip(col.status) ? 'button' : null" :tabindex="isStrip(col.status) ? 0 : null"
             :title="isStrip(col.status) ? stripTitle(col) : null"
             @click="isStrip(col.status) && toggleLane(col.status)"
             @keydown="onLaneKey(col, $event)">
          <!-- the strip face: the folded lane's count and name, read upward.
               the lane's scroll area (below) overlays the whole strip so a
               card can still be dropped here, and hovering a card over it
               for a moment opens the lane under the cursor (onDragMove). -->
          <template v-if="isStrip(col.status)">
            <i class="fa fa-angle-double-right stb-col-collapsed-icon" aria-hidden="true"></i>
            <span class="stb-col-collapsed-count">{{ columns[col.status].length }}</span>
            <span class="stb-col-collapsed-label">
              <i class="fa" :class="col.icon" aria-hidden="true"></i>&nbsp;{{ col.label }}
            </span>
          </template>
          <template v-else>
            <div class="stb-col-head">
              <span class="stb-status-chip" :class="'stb-status-chip--' + col.status">
                <i class="fa" :class="col.icon"></i>
                {{ col.label }}
              </span>
              <span class="stb-col-head-right">
                <span class="stb-col-count">{{ columns[col.status].length }}</span>
                <button type="button" class="stb-col-fold" :title="'Collapse ' + col.label"
                        :aria-label="'Collapse ' + col.label" @click.stop="toggleLane(col.status)">
                  <i class="fa fa-angle-double-left" aria-hidden="true"></i>
                </button>
              </span>
            </div>
            <!-- while a card is in flight the note stops describing the lane
                 and starts answering "what happens if I let go here?" -->
            <div class="stb-col-note" :class="noteStateClass(col.status)">
              <span>{{ laneNote(col) }}</span>
              <!-- the closed window is a view setting on the lane it bounds,
                   so its picker lives here rather than up in the filters.
                   both closed lanes share one window and move together. the
                   drag note owns the line while a card is in flight. -->
              <span v-if="col.closed && !draggingFrom" class="stb-col-window" role="group"
                    :aria-label="col.label + ' window'">
                <button v-for="w in CLOSED_WINDOWS" :key="w" type="button"
                        class="stb-col-window-btn" :class="{ 'stb-col-window-btn--active': closedWindowDays === w }"
                        :aria-pressed="closedWindowDays === w ? 'true' : 'false'"
                        :title="closedWindowTitle(w)"
                        @click.stop="closedWindowDays = w">{{ closedWindowLabel(w) }}</button>
              </span>
            </div>
          </template>
            <div class="stb-col-scroll"
                 :class="laneShadowClasses('stb-col-scroll', col.status)"
                 :ref="'laneScroll-' + col.status"
                 @scroll.passive="onLaneScroll(col.status, $event)">
            <!-- sort on the open lanes is the rank gesture; the closed lanes
                 keep newest-first and accept drops without reordering -->
            <draggable :list="columns[col.status]" :sort="canDrag && !col.closed && !col.locked"
                       :group="{ name: 'stb-board', pull: canDrag && !col.locked, put: canDrag && !col.locked }"
                       :disabled="!canDrag || saving || ranking || !!col.locked"
                       ghost-class="stb-card--ghost" drag-class="stb-card--dragging"
                       :animation="180"
                       :force-fallback="true" :fallback-on-body="true" :fallback-tolerance="4"
                       :move="onDragMove"
                       class="stb-col-body" @change="onColumnChange(col.status, $event)"
                       @start="onDragStart(col.status)" @end="onDragEnd">
              <div v-for="t in columns[col.status]" :key="ticketKey(t)"
                   class="stb-card"
                   :class="{
                     'stb-card--closed': col.closed,
                     'stb-card--major': !col.closed && isMajor(t),
                     'stb-card--stale': !col.closed && !isMajor(t) && isStale(t),
                     'stb-card--locked': !canDrag,
                     'stb-card--landed': flashes[ticketKey(t)] === 'landed',
                     'stb-card--rejected': flashes[ticketKey(t)] === 'rejected',
                   }"
                   @click="openTicket(t)">
                <!-- the id leads; the type / urgency tags sit with the
                     feature chip in the meta row below the subject -->
                <div class="stb-card-top">
                  <span class="stb-card-id" :title="'Created ' + formatDateTime(t.created_at)">#{{ t.id }}</span>
                  <!-- when it was opened and when it last moved, top right
                       (the close date on a closed lane). one date when both
                       fall on the same day. -->
                  <span class="stb-card-updated ml-auto" :title="cardDatesTitle(t, col.closed)">
                    <span :class="{ 'stb-card-updated--closed': col.closed && !cardDates(t).range }">{{ cardDates(t).created }}</span>
                    <template v-if="cardDates(t).range">
                      <span class="stb-card-dates-arrow" aria-hidden="true">&rarr;</span>
                      <span :class="{ 'stb-card-updated--closed': col.closed }">{{ cardDates(t).last }}</span>
                    </template>
                  </span>
                </div>

                <div class="stb-card-subject">{{ t.subject || '(no subject)' }}</div>

                <div v-if="t.priority || t.urgency || t.category || (isAdminView && (t.billable || t.estimated_finish_date))" class="stb-card-meta">
                  <!-- type and urgency as their glyphs alone, in the tag box;
                       the full names are one hover away -->
                  <span v-if="priorityTag(t.priority)" class="stb-tag stb-tag--icon" :class="priorityTag(t.priority).variant"
                        :title="'Type: ' + priorityTag(t.priority).label" :aria-label="'Type: ' + priorityTag(t.priority).label">
                    <i class="fa" :class="priorityTag(t.priority).icon" aria-hidden="true"></i>
                  </span>
                  <span v-if="isAdminView && urgencyTag(t.urgency)" class="stb-tag stb-tag--icon" :class="urgencyTag(t.urgency).variant"
                        :title="'Urgency: ' + urgencyTag(t.urgency).label" :aria-label="'Urgency: ' + urgencyTag(t.urgency).label">
                    <i class="fa" :class="urgencyTag(t.urgency).icon" aria-hidden="true"></i>
                  </span>
                  <span v-if="t.category" class="stb-cat-chip" :class="'stb-feat-' + t.category">{{ categoryLabel(t.category) }}</span>
                  <span v-if="isAdminView && t.billable" class="stb-tag stb-tag-success stb-tag--billable" title="Billable">
                    <i class="fa fa-usd" aria-hidden="true"></i>
                  </span>
                  <span v-if="isAdminView && t.estimated_finish_date" class="stb-meta" title="Target date">
                    <i class="fa fa-calendar-check-o" aria-hidden="true"></i>&nbsp;{{ formatTarget(t.estimated_finish_date) }}
                  </span>
                </div>

                <div class="stb-card-foot">
                  <!-- whose card this is, bottom-left: the account (when the
                       board spans more than one) and under it, smaller, who
                       opened the ticket -->
                  <span v-if="showTenantOnCards || createdByLabel(t)" class="stb-card-who">
                    <span v-if="showTenantOnCards" class="stb-card-tenant"
                          :class="{ 'stb-card-tenant--vip': tenantVip(t.tenant_id) }"
                          :title="tenantVip(t.tenant_id) ? (tenantVip(t.tenant_id).vip_reason || 'VIP account') : tenantName(t.tenant_id)">
                      <i v-if="tenantVip(t.tenant_id)" class="fa fa-star" aria-hidden="true"></i>
                      {{ tenantName(t.tenant_id) }}
                    </span>
                    <span v-if="createdByLabel(t)" class="stb-card-creator" :title="createdByTitle(t)">
                      {{ createdByLabel(t) }}
                    </span>
                  </span>
                  <span v-if="!col.closed && isStale(t)" class="stb-flag stb-flag--stale"
                        :title="'Last updated ' + formatDateTime(t.updated_at)">{{ daysSinceUpdate(t) }}d quiet</span>
                  <!-- the assignee is their photo alone (name on hover); the
                       viewer's own cards ring it. an assignee without a
                       photo falls back to their name; nobody is the
                       struck-out head the filter strip uses.
                       for staff the face is also the reassign control: it
                       becomes a button that opens the roster picker over
                       the board (the caret says so on hover). everyone else
                       keeps the plain span, so a click there still opens the
                       ticket the way the rest of the card does. -->
                  <!-- the right edge is one group: the actual dev hours
                       (staff only, once recorded) and then the face -->
                  <span class="ml-auto stb-card-foot-end">
                    <span v-if="isAdminView && actualHoursLabel(t)" class="stb-meta stb-card-hours"
                          :title="'Actual dev: ' + actualHoursLabel(t)" :aria-label="'Actual dev: ' + actualHoursLabel(t)">
                      {{ actualHoursLabel(t) }}
                    </span>
                    <component :is="isAdminView ? 'button' : 'span'"
                               class="stb-assignee-wrap"
                               :class="{
                                 'stb-assignee-wrap--btn': isAdminView,
                                 'stb-assignee-wrap--open': assigneePickerKey === ticketKey(t),
                                 'stb-assignee-wrap--saving': !!assigning[ticketKey(t)],
                               }"
                               v-bind="assigneeTriggerAttrs(t)"
                               @click="onAssigneeClick(t, $event)">
                      <img v-if="teamImage(t.assigned_to)" :src="teamImage(t.assigned_to)" class="stb-avatar"
                           :class="{ 'stb-avatar--mine': isMine(t) }"
                           :alt="displayUserName(t.assigned_to)"
                           :title="assigneeHint(displayUserName(t.assigned_to) + (isMine(t) ? ' (you)' : ''))">
                      <span v-else-if="t.assigned_to" class="stb-assignee" :class="{ 'stb-you-chip': isMine(t) }"
                            :title="assigneeHint('Assigned to ' + displayUserName(t.assigned_to))">{{ displayUserName(t.assigned_to) }}</span>
                      <span v-else class="stb-avatar stb-avatar--none" :title="assigneeHint('Unassigned')" aria-label="Unassigned">
                        <i class="fa fa-user" aria-hidden="true"></i>
                      </span>
                      <i v-if="isAdminView" class="fa fa-caret-down stb-assignee-caret" aria-hidden="true"></i>
                    </component>
                  </span>
                </div>
              </div>
            </draggable>

            <div v-if="!columns[col.status].length && !loading && !draggingFrom && !isStrip(col.status)" class="stb-col-empty">
              {{ laneEmptyText(col) }}
            </div>
            </div>
          </div>
      </div>
    </div>

    <!-- one reassign picker for the whole board, anchored to the face that
         opened it (the vsm board's status-picker recipe). position: fixed and
         rendered at the root, so it escapes the lane's overflow scrollport
         instead of being clipped by it, and opening it never reflows the card
         underneath. -->
    <div v-if="assigneePickerTicket" ref="assigneePop" class="stb-assign-pop"
         :style="assigneePickerStyle" role="menu"
         :aria-label="`Assign ticket #${assigneePickerTicket.id}`"
         @click.stop @keydown.esc="closeAssigneePicker(true)">
      <div class="stb-assign-pop-head">Assign #{{ assigneePickerTicket.id }}</div>
      <button v-for="opt in assigneeOptions" :key="opt.value || '__none__'" type="button"
              class="stb-assign-opt"
              :class="{ 'stb-assign-opt--current': isCurrentAssignee(assigneePickerTicket, opt.value) }"
              role="menuitemradio"
              :aria-checked="isCurrentAssignee(assigneePickerTicket, opt.value) ? 'true' : 'false'"
              @click="pickAssignee(opt.value)">
        <img v-if="opt.image" :src="opt.image" class="stb-avatar" alt="">
        <span v-else class="stb-avatar stb-avatar--none" aria-hidden="true">
          <i class="fa fa-user"></i>
        </span>
        <span class="stb-assign-opt-name">{{ opt.text }}</span>
        <i v-if="isCurrentAssignee(assigneePickerTicket, opt.value)" class="fa fa-check stb-assign-opt-check" aria-hidden="true"></i>
      </button>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment'
import draggable from 'vuedraggable'
import {
  search_support_tickets, get_support_ticket, update_support_ticket, rank_support_tickets,
  get_support_ticket_billable_summary,
} from '../networking/apis'
import { tenantDisplayName } from '../utils/tenant_context'
import swimLaneViewport from '../utils/swim_lane_viewport'
import { INITEK_TEAM_MEMBERS, INITEK_TEAM_FILTER_CHIPS, initekTeamImageForUsername } from '../helpers/initek_team_images'
import { rankOf, openSort, closedSort, mergeLaneOrder } from '../utils/support_board_order'
import SupportTicketModal from './support_ticket_modal.vue'
import { ticketInvolves, ticketPeople } from '@/helpers/support_tickets'
import SupportTicketCreateModal from './support_ticket_create_modal.vue'
import SupportViewToggle from './support_view_toggle.vue'

// one lane per ticket_status, in workflow order. labels are the list's
// status_options verbatim so the two surfaces call a state the same thing.
// closed lanes are windowed and unranked: they are a recent-closures view,
// not an archive (the list is the archive surface).
// Blocked sits right after New: a blocked ticket is one that needs a
// decision before it can even be queued, so it belongs with the intake.
// Draft: tickets staff have written but not sent (the billing audits open
// theirs here). staff-only, and locked: nothing is dragged in or out,
// because leaving draft IS the send (the ticket page's Send to customer).
export const LANES = [
  { status: 'draft',     label: 'Draft',               icon: 'fa-pencil',       note: 'written by staff, not sent to the customer yet', empty: 'No drafts', staff: true, locked: true },
  { status: 'new',       label: 'New',                 icon: 'fa-inbox',        note: 'filed, nobody has triaged it',       empty: 'No new tickets' },
  { status: 'blocked',   label: 'Blocked',             icon: 'fa-ban',          note: 'stuck on something',                 empty: 'Nothing blocked' },
  { status: 'queued',    label: 'Queued',              icon: 'fa-list-ol',      note: 'triaged, waiting for a slot',        empty: 'Nothing queued' },
  { status: 'review',    label: 'Under Review',        icon: 'fa-search',       note: 'being looked into',                  empty: 'Nothing under review' },
  { status: 'progress',  label: 'In Progress',         icon: 'fa-cogs',         note: 'somebody is working it',             empty: 'Nothing in progress' },
  { status: 'waiting',   label: 'Waiting On Customer', icon: 'fa-clock-o',      note: 'waiting on the customer',            empty: 'Nothing waiting on a customer' },
  { status: 'testing',   label: 'Customer Testing',    icon: 'fa-flask',        note: 'delivered, being verified',          empty: 'Nothing in testing' },
  { status: 'completed', label: 'Completed',           icon: 'fa-check',        note: 'closed in the last 7 days', closed: true },
  { status: 'cancelled', label: 'Cancelled',           icon: 'fa-times',        note: 'cancelled in the last 7 days', closed: true },
]
// the closed lanes' note, for a window of `days` (0 = every closure)
export function closedLaneNote(status, days) {
  const verb = status === 'cancelled' ? 'cancelled' : 'closed'
  if (!days) return status === 'cancelled' ? 'every cancellation, newest first' : 'every closure, newest first'
  return `${verb} in the last ${days} days`
}
const CLOSED_STATUSES = LANES.filter(l => l.closed).map(l => l.status)
const LOCKED_STATUSES = LANES.filter(l => l.locked).map(l => l.status)

// the retired Customer Approved status. a ticket still carrying it (written
// before it was dropped) shows in the Queued lane, and the first drag or
// save writes it back as queued. scripts/migrate_approved_to_queued.py in
// fleet-int-api rewrites the stored rows.
const RETIRED_STATUSES = { approved: 'queued' }
export function laneStatus(status) {
  return RETIRED_STATUSES[status] || status
}

// what a lane promises while a card is hovering over it
const DROP_NOTES = {
  new: 'Drop here to mark new',
  queued: 'Drop here to queue it',
  review: 'Drop here to put it under review',
  progress: 'Drop here to start work',
  waiting: 'Drop here to wait on the customer',
  testing: 'Drop here to hand over for testing',
  blocked: 'Drop here to mark blocked',
  completed: 'Drop here to complete it',
  cancelled: 'Drop here to cancel it',
}

// the list's tag families, so a card reads the same as its row
const PRIORITY_TAGS = {
  question:    { label: 'Question/Request',          compactLabel: '?',        icon: 'fa-question', variant: 'stb-tag-warning' },
  minor:       { label: 'Minor Bug',                 compactLabel: 'MINOR',    icon: 'fa-bug',      variant: 'stb-tag-orange' },
  urgent:      { label: 'Major Bug / System Issue',  compactLabel: 'MAJOR',    icon: 'fa-bolt',     variant: 'stb-tag-danger' },
  enhancement: { label: 'Enhancement Request',       compactLabel: 'ENHANCE',  icon: 'fa-wrench',   variant: 'stb-tag-info' },
  feature:     { label: 'New Feature Request',       compactLabel: 'FEATURE',  icon: 'fa-star',     variant: 'stb-tag-success' },
}
const URGENCY_TAGS = {
  low:       { label: 'Low',       compactLabel: 'LOW',       icon: 'fa-minus',                variant: 'stb-tag-info' },
  medium:    { label: 'Medium',    compactLabel: 'MED',       icon: 'fa-exclamation',          variant: 'stb-tag-warning' },
  high:      { label: 'High',      compactLabel: 'HIGH',      icon: 'fa-exclamation-triangle', variant: 'stb-tag-orange' },
  emergency: { label: 'Emergency', compactLabel: 'EMERG',     icon: 'fa-bolt',                 variant: 'stb-tag-danger' },
}

// the list's Type / Urgency vocabularies as toggles, each wearing its
// tag's colour and glyph so the chip row reads like the cards
const TYPE_FILTERS = [
  { value: 'urgent', text: 'Major Bug' },
  { value: 'minor', text: 'Minor Bug' },
  { value: 'question', text: 'Question' },
  { value: 'enhancement', text: 'Enhancement' },
  { value: 'feature', text: 'Feature' },
].map(o => ({ ...o, icon: PRIORITY_TAGS[o.value].icon, variant: PRIORITY_TAGS[o.value].variant }))
const URGENCY_FILTERS = [
  { value: 'emergency', text: 'Emergency' },
  { value: 'high', text: 'High' },
  { value: 'medium', text: 'Medium' },
  { value: 'low', text: 'Low' },
].map(o => ({ ...o, icon: URGENCY_TAGS[o.value].icon, variant: URGENCY_TAGS[o.value].variant }))

// the Status chips: fold presets. each names the lanes it opens; every
// other lane folds to a strip. the same groups the list's dropdown uses.
export const STATUS_PRESETS = {
  '': LANES.map(l => l.status),
  open: LANES.filter(l => !l.closed && !l.locked).map(l => l.status),
  // Under Review stays folded under Active: it is a holding state, not
  // work in hand, and the three working lanes want the width
  active: ['new', 'queued', 'progress'],
}
const STATUS_FILTERS = [
  { value: '', text: 'All', title: 'Open every lane' },
  { value: 'open', text: 'Open', title: 'Fold Draft, Completed and Cancelled' },
  { value: 'active', text: 'Active', title: 'Only New, Queued and In Progress open' },
]

// the Initek roster, pinned to the top of the Created By picker
// concat rather than flatMap: the e2e runner's electron 61 predates Array.prototype.flatMap
const INITEK_USERNAMES = new Set([].concat(...INITEK_TEAM_MEMBERS.map(m => m.usernames)))
const CREATED_BY_SEPARATOR = '__sep__'
// the Feature chips: the list's category vocabulary minus its "All"
// sentinel (an empty chip set means every feature)
const CATEGORY_OPTIONS = [
  { value: '', text: 'All features' },
  { value: 'general', text: 'General' },
  { value: 'actuals', text: 'Actuals' },
  { value: 'alerts', text: 'Alerts' },
  { value: 'maps', text: 'Map Views' },
  { value: 'reports', text: 'View & Reports' },
  { value: 'vsm', text: 'VSM' },
  { value: 'data', text: 'Data Services' },
  { value: 'fleet-assistant', text: 'Fleet Assistant' },
  { value: 'driver-scorecard', text: 'Driver Manager' },
  { value: 'coaching-portal', text: 'Coaching Portal' },
  { value: 'erm', text: 'ERM' },
  { value: 'bus-sos', text: 'Bus SOS' },
]
// the chips themselves: the vocabulary minus the "All" sentinel. declared
// after CATEGORY_OPTIONS -- a const read before its line throws at module
// load, and this module loads with the router, which takes the app down.
const FEATURE_FILTERS = CATEGORY_OPTIONS.filter(o => o.value)
// how many feature chips the row shows before folding the rest
const FEATURE_CHIPS_SHOWN = 3

// staff display names, keyed by the stored assigned_to value
const ASSIGNEE_NAMES = {}
INITEK_TEAM_MEMBERS.forEach(m => m.usernames.forEach(u => { ASSIGNEE_NAMES[u] = m.name }))

// an open ticket with no update this long has stopped moving. board-only,
// the way the erm board reads stale -- nothing on the server knows it.
const STALE_AFTER_DAYS = 14

// the closed lanes are a recent-closures view rather than an archive: the
// last 7 days by default, widened from the lane's own picker to 30 or 90
// days, or lifted altogether (0). the list is still the archive surface
// proper.
export const CLOSED_WINDOW_DEFAULT = 7
export const CLOSED_WINDOWS = [7, 30, 90, 0]

// drag is off at or below this width. matches the @media (max-width: 768px)
// block at the foot of this file -- the two must agree.
const MOBILE_MAX_WIDTH = 768

const FLASH_MS = 1500
// how long a dragged card rests over a folded lane before it opens
const HOVER_OPEN_MS = 350

const FILTERS_KEY = 'support-board-filters'

export default {
  name: 'support_ticket_board',
  components: { draggable, SupportTicketModal, SupportTicketCreateModal, SupportViewToggle },
  mixins: [swimLaneViewport],
  data() {
    const columns = {}
    LANES.forEach(l => { columns[l.status] = [] })
    return {
      loading: false,
      error: null,
      // a status change is in flight (drag disabled meanwhile)
      saving: false,
      // a rank write is in flight
      ranking: false,
      tickets: [],

      // invoice prep modal (staff): the month being summarised and its result
      billableMonth: '',
      // '' = everyone, else the stored assigned_to value (a roster username)
      billableAssignee: '',
      billableSummary: null,
      billableLoading: false,
      billableError: null,
      billableCopied: false,

      // the swim lanes. draggable mutates these arrays on drop, so they are
      // plain data rebuilt from `tickets` -- never computed.
      columns,

      // staff: '' = every customer, else one tenant_id. non-staff never
      // see the picker; their query is their own tenant.
      selectedTenant: '',

      search: '',
      filtersExpanded: false,
      isMobileView: false,

      typeFilters: [],
      urgencyFilters: [],
      // the last Status preset picked (a chip label, not a filter): the
      // fold state itself lives in collapsedLanes and is free to drift
      // from it as lanes are opened and folded by hand. Active by default.
      statusFilter: 'active',
      STATUS_FILTERS,
      // empty = every feature; otherwise the selected category values
      categoryFilters: [],
      // the Feature row shows three chips until this opens it
      featuresExpanded: false,
      FEATURE_FILTERS,
      vipOnly: false,
      billableOnly: false,
      // the closed lanes' window in days (0 = every closure), picked on
      // the lane itself
      closedWindowDays: CLOSED_WINDOW_DEFAULT,
      CLOSED_WINDOWS,
      notifyOnMove: false,
      TYPE_FILTERS,
      URGENCY_FILTERS,

      // '' = everyone, MINE = the signed-in user, MY_CREATED = tickets whose
      // Created By (requester) is the signed-in user, UNASSIGNED = no
      // assignee, any other string = that user's username
      assigneeFilter: '',
      // '' = anyone; else the exact stored username of the ticket's creator
      createdByFilter: '',
      MINE: '__mine__',
      MY_CREATED: '__my_created__',
      UNASSIGNED: '__unassigned__',
      scopeInitialized: false,

      // lanes folded to a strip. a first visit opens the Active preset's
      // three working lanes and folds the rest; from there the board
      // remembers whatever the viewer folds and opens.
      collapsedLanes: LANES.map(l => l.status).filter(st => !STATUS_PRESETS.active.includes(st)),

      // the open reassign picker: the card it belongs to, the key that
      // marks its trigger held, and the fixed position it was placed at
      assigneePickerTicket: null,
      assigneePickerKey: null,
      assigneePickerStyle: null,
      // the cards whose assignment is being written, by key (the `flashes`
      // pattern). a map rather than one key: two tickets reassigned in quick
      // succession are independent writes, and the second must not be
      // silently dropped because the first is still in the air.
      assigning: {},

      draggingFrom: null,
      // the folded lane a dragged card has hovered long enough to open.
      // one at a time; cleared when the drag ends.
      hoverExpanded: null,
      flashes: {},
      flashTimers: [],

    }
  },
  computed: {
    ...mapState({ user: state => state.profile.user }),
    // admins can preview as a non-admin via the navbar toggle; every admin
    // gate here reads this rather than user.role so the preview holds.
    effectiveRole() {
      const viewAs = this.$store && this.$store.state && this.$store.state.profile && this.$store.state.profile.view_as
      if (this.user && this.user.role === 'admin' && viewAs === 'non-admin') return 'customer'
      return this.user && this.user.role
    },
    isAdminView() {
      return this.effectiveRole === 'admin'
    },
    // the invoice modal's month picker: the last 12 months, newest first
    billableMonthOptions() {
      const options = []
      const cursor = moment().startOf('month')
      for (let i = 0; i < 12; i++) {
        options.push({ value: cursor.format('YYYY-MM'), text: cursor.format('MMMM YYYY') })
        cursor.subtract(1, 'month')
      }
      return options
    },
    billableMonthLabel() {
      const opt = this.billableMonthOptions.find(o => o.value === this.billableMonth)
      return opt ? opt.text : this.billableMonth
    },
    // everyone, then the roster, on the same assigned_to values the board's
    // assignee chips filter by
    billableAssigneeOptions() {
      const rows = [{ value: '', text: 'Everyone' }]
      this.teamChips.forEach(m => rows.push({ value: m.username, text: m.name }))
      return rows
    },
    billableAssigneeLabel() {
      if (!this.billableAssignee) return ''
      const opt = this.billableAssigneeOptions.find(o => o.value === this.billableAssignee)
      return ' for ' + (opt ? opt.text : this.billableAssignee)
    },
    billableText() {
      return (this.billableSummary && this.billableSummary.text) || ''
    },
    // billable tickets that closed in the month with no hours entered, as
    // "Customer #1799, #1800; Other Customer #1234" -- those lines print as
    // 0 hrs, so the gap is called out above the text rather than hidden in it
    billableMissingHours() {
      const missing = (this.billableSummary && this.billableSummary.missing_hours) || []
      if (!missing.length) return ''
      const byTenant = {}
      missing.forEach(m => { (byTenant[m.tenant_name] = byTenant[m.tenant_name] || []).push('#' + m.id) })
      return Object.keys(byTenant).map(name => name + ' ' + byTenant[name].join(', ')).join('; ')
    },
    // the drag surface: staff only, and never on touch widths (sortablejs
    // starts a drag on the first touch move, which on a phone is a scroll)
    canDrag() {
      return this.isAdminView && !this.isMobileView
    },
    // the tenant the api is asked for. '' for staff means every customer
    // (search_support_tickets skips the tenant clause for an admin).
    // staff load every customer's tickets once and narrow by customer on
    // the client (the picker's counts need the whole set; the api's tenant
    // clause was a post-scan filter anyway, so this costs nothing more).
    // everyone else's query is their own tenant.
    queryTenant() {
      if (this.isAdminView) return ''
      return this.user ? this.user.tenant : ''
    },
    contextTenantName() {
      if (!this.isAdminView || !this.selectedTenant) return ''
      return tenantDisplayName(this.user, this.selectedTenant)
    },
    // the customer the navbar switcher is on. staff load every customer's
    // tickets, so this narrows the board rather than the query -- it is
    // the Current chip's value, and the picker holds the same field.
    currentTenant() {
      return (this.user && this.user.tenant) || ''
    },
    currentTenantName() {
      return this.currentTenant ? this.tenantName(this.currentTenant) : ''
    },
    currentTenantOnly() {
      return !!this.currentTenant && this.selectedTenant === this.currentTenant
    },
    // customers with a card on the board (after every other filter), each
    // with its count; the current pick stays listed even at zero
    tenantOptions() {
      const rows = this.onOpenLanes(this.applyFilters(this.tickets, 'tenant'))
      const counts = {}
      rows.forEach(t => { if (t.tenant_id) counts[t.tenant_id] = (counts[t.tenant_id] || 0) + 1 })
      if (this.selectedTenant && !counts[this.selectedTenant]) counts[this.selectedTenant] = 0
      const options = Object.keys(counts)
        .map(id => ({ value: id, text: `${this.tenantName(id)} (${counts[id]})`, name: this.tenantName(id) }))
        .sort((a, b) => a.name.localeCompare(b.name))
        .map(({ value, text }) => ({ value, text }))
      return [{ value: '', text: `All customers (${rows.length})` }, ...options]
    },
    // the account line on a card is only information when the board could
    // be showing more than one account
    showTenantOnCards() {
      if (this.isAdminView) return !this.selectedTenant
      return ((this.user && this.user.tenant_options) || []).length > 1
    },
    // the All preset opens every lane at once, which is where the sideways
    // scroll gets long: the lanes give up a little width so more of them
    // land on screen. it follows the chip, not the folds -- All means the
    // viewer asked for the whole board.
    denseLanes() {
      return this.statusFilter === ''
    },
    columnDefs() {
      return LANES.filter(l => !l.staff || this.isAdminView).map(l => {
        if (!l.closed) return { ...l }
        const days = this.closedWindowDays
        return {
          ...l,
          note: closedLaneNote(l.status, days),
          empty: days ? `No closures in the last ${days} days` : 'No closures',
        }
      })
    },
    // the Mine-by-default seed: the viewer's open work, before any filter
    myOpenCount() {
      return this.tickets.filter(t => this.inMyScope(t) && !CLOSED_STATUSES.includes(laneStatus(t.ticket_status))).length
    },
    // every chip and picker counts the cards it would leave on the board:
    // the rows that survive every OTHER filter (skip names the chip's own
    // dimension), so picking a value never collapses its own row of
    // counts to zero. closed lanes count within their window, like the
    // board itself.
    assigneeCounts() {
      const rows = this.onOpenLanes(this.applyFilters(this.tickets, 'assignee'))
      const counts = { '': rows.length, [this.MINE]: 0, [this.MY_CREATED]: 0, [this.UNASSIGNED]: 0 }
      rows.forEach(t => {
        if (!t.assigned_to) counts[this.UNASSIGNED] += 1
        // a photo chip counts the cards that person is on in any role
        ticketPeople(t).forEach(p => { counts[p] = (counts[p] || 0) + 1 })
        if (this.inMyScope(t)) counts[this.MINE] += 1
        if (this.createdByMe(t)) counts[this.MY_CREATED] += 1
      })
      return counts
    },
    // a Status chip counts the cards on the lanes it would open, after
    // every other filter -- what the board would show once it is pressed
    statusCounts() {
      const counts = {}
      Object.keys(STATUS_PRESETS).forEach(k => { counts[k] = 0 })
      this.scopedTickets.forEach(t => {
        const lane = laneStatus(t.ticket_status)
        Object.keys(STATUS_PRESETS).forEach(k => {
          if (STATUS_PRESETS[k].includes(lane)) counts[k] += 1
        })
      })
      return counts
    },
    flagCounts() {
      return {
        vip: this.onOpenLanes(this.applyFilters(this.tickets, 'vip')).filter(t => !!this.tenantVip(t.tenant_id)).length,
        billable: this.onOpenLanes(this.applyFilters(this.tickets, 'billable')).filter(t => !!t.billable).length,
        // counted the way the Customer picker counts its options: every
        // other filter applied, the tenant one skipped, so the number is
        // what pressing the chip would leave on the board
        current: this.currentTenant
          ? this.onOpenLanes(this.applyFilters(this.tickets, 'tenant'))
            .filter(t => t.tenant_id === this.currentTenant).length
          : 0,
      }
    },
    featureCounts() {
      const counts = {}
      this.onOpenLanes(this.applyFilters(this.tickets, 'feature')).forEach(t => {
        if (t.category) counts[t.category] = (counts[t.category] || 0) + 1
      })
      return counts
    },
    // the feature chips worth showing: those with a card behind them, plus
    // any that are pressed (so they can be unpressed)
    availableFeatureFilters() {
      return FEATURE_FILTERS.filter(o => this.featureCounts[o.value] || this.categoryFilters.includes(o.value))
    },
    // collapsed: the first three in vocabulary order, and every pressed
    // one wherever it sits; expanded: all of them
    shownFeatureFilters() {
      if (this.featuresExpanded) return this.availableFeatureFilters
      return this.availableFeatureFilters.filter((o, i) => i < FEATURE_CHIPS_SHOWN || this.categoryFilters.includes(o.value))
    },
    hiddenFeatureCount() {
      return this.availableFeatureFilters.length - this.shownFeatureFilters.length
    },
    typeCounts() {
      const counts = {}
      this.onOpenLanes(this.applyFilters(this.tickets, 'type')).forEach(t => {
        counts[t.priority] = (counts[t.priority] || 0) + 1
      })
      return counts
    },
    urgencyCounts() {
      const counts = {}
      this.onOpenLanes(this.applyFilters(this.tickets, 'urgency')).forEach(t => {
        const u = t.urgency || 'low'
        counts[u] = (counts[u] || 0) + 1
      })
      return counts
    },
    scopedTickets() {
      return this.applyFilters(this.tickets)
    },
    matchCount() {
      return this.onOpenLanes(this.scopedTickets).length
    },
    // the narrowing controls proper. the assignee scope, the customer
    // picker and the closed window always hold a value, so they are not
    // counted here -- counting them would make the board permanently
    // "filtered" in the nothing-matches sense.
    isFiltered() {
      return !!(this.search.trim() || this.typeFilters.length || this.urgencyFilters.length
        || this.categoryFilters.length || this.vipOnly || this.billableOnly || this.createdByFilter)
    },
    // badge on the mobile toggle: what is hidden behind it
    activeFilterCount() {
      let n = 0
      if (this.search.trim()) n++
      if (this.typeFilters.length) n++
      if (this.urgencyFilters.length) n++
      if (this.categoryFilters.length) n++
      if (this.isAdminView) {
        if (this.vipOnly) n++
        if (this.billableOnly) n++
        if (this.assigneeFilter) n++
        if (this.selectedTenant) n++
        if (this.createdByFilter) n++
      }
      return n
    },
    // the strip's people: the list's chip roster, each with the stored
    // assigned_to value it filters on (a member's first username)
    teamChips() {
      return INITEK_TEAM_FILTER_CHIPS.map(m => ({ ...m, username: m.usernames[0] }))
    },
    // the reassign picker's rows: nobody, then the roster. a ticket already
    // assigned to somebody off the roster (a departed teammate, a hand-written
    // value) gets a row of their own so the picker can always show what the
    // card currently says -- and so picking someone else is never blocked by
    // a value the list has forgotten about.
    assigneeOptions() {
      const rows = [{ value: null, text: 'Unassigned', image: null }]
      this.teamChips.forEach(m => rows.push({ value: m.username, text: m.name, image: m.image }))
      const current = this.assigneePickerTicket && this.assigneePickerTicket.assigned_to
      if (current && !rows.some(r => r.value === current)) {
        rows.push({ value: current, text: this.displayUserName(current), image: this.teamImage(current) })
      }
      return rows
    },
    // every narrowing control that is set, as a removable pill. the Status
    // preset leads it -- it is a view setting rather than a filter, but it
    // is the first thing that decides what is on screen, and its × opens
    // every lane. the notify checkbox is a setting and does not appear.
    activeFilterPills() {
      const pills = []
      const add = (key, dim, text, field, value) => pills.push({ key, dim, text, field, value })
      // in the order the controls sit on screen, left to right, row by row
      if (this.isAdminView) {
        if (this.assigneeFilter === this.MINE) add('assignee', 'Person', 'Me', 'assigneeFilter')
        else if (this.assigneeFilter === this.MY_CREATED) add('assignee', 'Created', 'By me', 'assigneeFilter')
        else if (this.assigneeFilter === this.UNASSIGNED) add('assignee', 'Assigned', 'Unassigned', 'assigneeFilter')
        else if (this.assigneeFilter) add('assignee', 'Person', this.displayUserName(this.assigneeFilter), 'assigneeFilter')
      }
      if (this.statusFilter) {
        const opt = STATUS_FILTERS.find(o => o.value === this.statusFilter)
        add('status', 'Status', opt ? opt.text : this.statusFilter, 'statusFilter')
      }
      if (this.isAdminView) {
        this.urgencyFilters.forEach(v => {
          const opt = URGENCY_FILTERS.find(o => o.value === v)
          add('urgency-' + v, 'Urgency', opt ? opt.text : v, 'urgencyFilters', v)
        })
        if (this.vipOnly) add('vip', 'Flag', 'VIP', 'vipOnly')
        if (this.billableOnly) add('billable', 'Flag', 'Billable', 'billableOnly')
      }
      if (this.search.trim()) add('search', 'Search', `“${this.search.trim()}”`, 'search')
      this.typeFilters.forEach(v => {
        const opt = TYPE_FILTERS.find(o => o.value === v)
        add('type-' + v, 'Type', opt ? opt.text : v, 'typeFilters', v)
      })
      this.categoryFilters.forEach(v => add('feature-' + v, 'Feature', this.categoryLabel(v), 'categoryFilters', v))
      if (this.isAdminView) {
        if (this.selectedTenant) add('tenant', 'Customer', this.tenantName(this.selectedTenant), 'selectedTenant')
        if (this.createdByFilter && this.createdByFilter !== CREATED_BY_SEPARATOR) {
          const opt = this.createdByOptions.find(o => o.value === this.createdByFilter)
          add('createdBy', 'Created by', opt ? opt.text.replace(/ \(\d+\)$/, '') : this.displayUserName(this.createdByFilter), 'createdByFilter')
        }
      }
      return pills
    },
    // the Created By picker: the Initek roster pinned above a separator,
    // then every other creator, each with the count of cards it would
    // leave on the board (rows surviving every other filter). the current
    // pick stays listed at zero rather than vanishing from under itself.
    createdByOptions() {
      const rows = this.onOpenLanes(this.applyFilters(this.tickets, 'createdBy'))
      const counts = {}
      const names = {}
      rows.forEach(t => {
        if (!t.username) return
        counts[t.username] = (counts[t.username] || 0) + 1
        names[t.username] = names[t.username] || t.created_by_name || this.displayUserName(t.username)
      })
      if (this.createdByFilter && this.createdByFilter !== CREATED_BY_SEPARATOR && !counts[this.createdByFilter]) {
        counts[this.createdByFilter] = 0
        names[this.createdByFilter] = this.displayUserName(this.createdByFilter)
      }
      const option = u => ({ value: u, text: `${names[u]} (${counts[u]})` })
      const byName = (a, b) => names[a].localeCompare(names[b])
      const initek = Object.keys(counts).filter(u => INITEK_USERNAMES.has(u)).sort(byName)
      const others = Object.keys(counts).filter(u => !INITEK_USERNAMES.has(u)).sort(byName)
      const options = [{ value: '', text: `Created by anyone (${rows.length})` }]
      initek.forEach(u => options.push(option(u)))
      if (initek.length && others.length) {
        options.push({ value: CREATED_BY_SEPARATOR, text: '──────────', disabled: true })
      }
      others.forEach(u => options.push(option(u)))
      return options
    },
  },
  watch: {
    // staff: the customer picker is a new query. everyone else: the navbar
    // tenant switcher mutates user.tenant in place without remounting the
    // page (ops_desk.vue precedent), and their query follows it.
    queryTenant() {
      this.tickets = []
      this.resetColumns()
      this.loadTickets()
    },
    assigneeFilter() { this.rebuildColumns(); this.saveFilters() },
    createdByFilter() { this.rebuildColumns(); this.saveFilters() },
    search() { this.rebuildColumns() },
    typeFilters() { this.rebuildColumns(); this.saveFilters() },
    urgencyFilters() { this.rebuildColumns(); this.saveFilters() },
    categoryFilters() { this.rebuildColumns(); this.saveFilters() },
    vipOnly() { this.rebuildColumns(); this.saveFilters() },
    billableOnly() { this.rebuildColumns(); this.saveFilters() },
    selectedTenant() { this.rebuildColumns(); this.saveFilters() },
    closedWindowDays() { this.rebuildColumns(); this.saveFilters() },
    collapsedLanes() { this.saveFilters() },
  },
  created() {
    // staff-only surface. the router guard bounces real non-admins; this
    // covers an admin previewing as a non-admin, who lands on the list.
    if (!this.isAdminView) {
      this.$router.replace({ name: 'help_center', query: { tab: 'support' } }).catch(() => {})
      return
    }
    this.loadFilters()
    // a remembered customer picker changes queryTenant, whose watcher
    // already loads on the next tick; only load here when it did not, or
    // the first visit would fire two identical requests
    this.$nextTick(() => { if (!this._loadGen) this.loadTickets() })
    // /support_ticket_board?ticket_id=N(&tenant_id=T) opens that ticket's
    // modal over the board -- the shape the help center uses, so a link
    // copied while the modal was open reopens it. tenant_id is a lookup
    // hint only; the api falls back to id-only when it misses.
    const q = this.$route.query || {}
    if (q.ticket_id) {
      this.openTicket({ id: q.ticket_id, tenant_id: q.tenant_id || (this.user && this.user.tenant) || '' }, { fromRoute: true })
    }
  },
  mounted() {
    // a ticket opened in the app-level modal and closed: patch its card
    this._ticketChangedHandler = (ref) => this.onTicketModalHidden(ref)
    this.$root.$on('support-ticket-changed', this._ticketChangedHandler)
    this._onResize = () => { this.isMobileView = window.innerWidth <= MOBILE_MAX_WIDTH }
    this._onResize()
    window.addEventListener('resize', this._onResize)
  },
  beforeDestroy() {
    if (this._ticketChangedHandler) this.$root.$off('support-ticket-changed', this._ticketChangedHandler)
    clearTimeout(this._hoverTimer)
    this.closeAssigneePicker()
    this.flashTimers.forEach(clearTimeout)
    if (this._onResize) window.removeEventListener('resize', this._onResize)
  },
  methods: {
    // ---- display helpers ------------------------------------------------
    ticketKey(t) {
      return `${t.tenant_id}#${t.id}`
    },
    priorityTag(priority) {
      return PRIORITY_TAGS[priority] || null
    },
    urgencyTag(urgency) {
      return urgency ? (URGENCY_TAGS[urgency] || null) : null
    },
    categoryLabel(value) {
      const opt = CATEGORY_OPTIONS.find(o => o.value === value)
      return opt ? opt.text : value
    },
    tenantName(tenantId) {
      const opt = ((this.user && this.user.tenant_options) || []).find(o => o && o.value === tenantId)
      return opt ? opt.text : tenantId
    },
    // VIP is a staff signal (login annotates tenant_options with is_vip);
    // gated on the effective role so the non-admin preview hides it too
    tenantVip(tenantId) {
      if (!this.isAdminView || !tenantId) return null
      const opt = ((this.user && this.user.tenant_options) || []).find(o => o && o.value === tenantId)
      return (opt && opt.is_vip) ? opt : null
    },
    // who opened the ticket: the name the ticket carries, else the one the
    // staff roster knows, else the address itself -- a strange email says
    // more about an unknown creator than its local part would.
    createdByLabel(t) {
      if (!t || !t.username) return ''
      return (t.created_by_name || '').trim() || ASSIGNEE_NAMES[t.username] || t.username
    },
    // the line is one ellipsis-prone row on a narrow card, so the hover
    // carries it in full, with the address behind a name
    createdByTitle(t) {
      const label = this.createdByLabel(t)
      if (!label) return ''
      return label === t.username ? `Created by ${label}` : `Created by ${label} (${t.username})`
    },
    displayUserName(username) {
      if (!username) return ''
      if (ASSIGNEE_NAMES[username]) return ASSIGNEE_NAMES[username]
      const at = String(username).indexOf('@')
      return at > 0 ? String(username).slice(0, at) : String(username)
    },
    teamImage(username) {
      return initekTeamImageForUsername(username)
    },
    isMine(t) {
      const me = this.user && this.user.username
      return !!(me && t.assigned_to === me)
    },
    // the Mine chip's scope: assigned to me, or I'm the requester (Created
    // By). the account that physically opened the ticket (created_by) does
    // not count, since the ui never shows it (ticketPeople, Ben 2026-09-25)
    inMyScope(t) {
      return ticketInvolves(t, this.user && this.user.username)
    },
    // the My Created chip's scope: tickets whose "Created By" user (the
    // requester, stored on username) is the signed-in user -- unlike Mine,
    // this ignores who the ticket is assigned to.
    createdByMe(t) {
      const me = this.user && this.user.username
      return !!(me && t.username === me)
    },
    // staff can click the face to reassign, so every tooltip on it says so.
    // everyone else reads the plain value.
    assigneeHint(base) {
      return this.isAdminView ? `${base} — click to reassign` : base
    },
    // the trigger is only a button for staff; a non-admin's face is a plain
    // span and must not claim a menu it cannot open
    assigneeTriggerAttrs(t) {
      if (!this.isAdminView) return {}
      const who = t.assigned_to ? this.displayUserName(t.assigned_to) : 'nobody'
      return {
        type: 'button',
        'aria-haspopup': 'menu',
        'aria-expanded': this.assigneePickerKey === this.ticketKey(t) ? 'true' : 'false',
        'aria-label': `Ticket #${t.id} assigned to ${who} — change assignee`,
        'data-assignee-trigger': this.ticketKey(t),
      }
    },
    isCurrentAssignee(t, value) {
      return !!t && (t.assigned_to || null) === (value || null)
    },
    isMajor(t) {
      return t.priority === 'urgent' || t.urgency === 'emergency'
    },
    daysSinceUpdate(t) {
      const raw = t.updated_at || t.created_at
      if (!raw) return 0
      return Math.max(0, moment().diff(moment(raw), 'days'))
    },
    isStale(t) {
      return this.daysSinceUpdate(t) >= STALE_AFTER_DAYS
    },
    formatDate(iso) {
      if (!iso) return '—'
      const m = moment(iso)
      if (!m.isValid()) return '—'
      return m.year() === moment().year() ? m.format('MMM D') : m.format('MMM D, YYYY')
    },
    formatDateTime(iso) {
      if (!iso) return '—'
      const m = moment(iso)
      return m.isValid() ? m.format('ddd, MMM D, YYYY · h:mm A') : '—'
    },
    // the card's two dates: opened, and the last change (the close stamp on
    // a closed lane). `range` is false when both land on the same day, so
    // the card shows that day once.
    cardDates(t) {
      const created = t.created_at
      const last = t.updated_at || t.created_at
      const range = !!(created && last && !moment(created).isSame(moment(last), 'day'))
      return { created: this.formatDate(created || last), last: this.formatDate(last), range }
    },
    cardDatesTitle(t, closed) {
      const last = t.updated_at || t.created_at
      return 'Created ' + this.formatDateTime(t.created_at)
        + ' · ' + (closed ? 'Closed ' : 'Updated ') + this.formatDateTime(last)
    },
    // target date is stored "MM-DD-YYYY" (support_ticket_list.vue)
    formatTarget(value) {
      if (!value || typeof value !== 'string') return '—'
      const m = moment(value, 'MM-DD-YYYY', true)
      return m.isValid() ? m.format('M/D') : '—'
    },
    // an open ticket is never windowed; a closed one is in only when its
    // close stamp falls inside the lane window (0 = no window)
    withinClosedWindow(t) {
      if (!CLOSED_STATUSES.includes(laneStatus(t.ticket_status))) return true
      const days = Number(this.closedWindowDays) || 0
      if (days <= 0) return true
      const closedAfter = moment().subtract(days, 'days').startOf('day')
      return moment(t.updated_at || t.created_at).isSameOrAfter(closedAfter)
    },
    closedWindowLabel(days) {
      return days ? `${days}d` : 'All'
    },
    closedWindowTitle(days) {
      return days ? `Closed in the last ${days} days` : 'Every closure, however old'
    },
    // actual_hours is a free-text field on the ticket (details view "Actual
    // Dev"). a number comes back as "3.5h"; a stray unit someone typed is
    // folded in rather than doubled; zero or blank is nothing to show
    actualHoursLabel(t) {
      const raw = t && t.actual_hours
      if (raw === null || raw === undefined) return ''
      const text = String(raw).trim().replace(/\s*(hours?|hrs?|h)$/i, '')
      if (!text) return ''
      const n = Number(text)
      if (!Number.isFinite(n)) return text
      if (n <= 0) return ''
      return Number(n.toFixed(2)) + 'h'
    },
    apiErrorMessage(e, fallback) {
      return (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || fallback
    },
    toast(message, variant, delay) {
      this.$bvToast.toast(message, { title: 'Ticket Board', variant, autoHideDelay: delay || 3000 })
    },

    // ---- filters --------------------------------------------------------
    // cards on folded lanes are out of sight, so they are out of the
    // counts too: a chip says how many cards it would leave on screen
    onOpenLanes(rows) {
      return rows.filter(t => !this.isCollapsed(laneStatus(t.ticket_status)))
    },

    // a chip that is already selected turns the scope back to everyone
    toggleAssignee(value) {
      this.assigneeFilter = this.assigneeFilter === value ? '' : value
    },
    // the viewer's own chip also reads as selected under the Mine scope
    isTeamChipSelected(member) {
      if (this.assigneeFilter === member.username) return true
      return this.assigneeFilter === this.MINE && this.user && this.user.username === member.username
    },
    // the list's own free-text rule: match what the card SHOWS as well as
    // the raw value, and an id exactly (ids are a dense counter, so "12"
    // must not drag in #112 and #1200).
    matchesSearch(t, needle) {
      const idQuery = needle.replace(/^#/, '')
      if (/^\d+$/.test(idQuery)) return String(t.id) === idQuery
      const haystack = [
        t.subject, t.description,
        t.tenant_id, t.tenant_id ? this.tenantName(t.tenant_id) : null,
        t.category, t.category ? this.categoryLabel(t.category) : null,
        t.username, t.created_by_name,
        t.assigned_to, t.assigned_to ? this.displayUserName(t.assigned_to) : 'Unassigned',
      ]
      return haystack.some(v => v != null && String(v).toLowerCase().includes(needle))
    },
    // one filter pipeline for every narrowing control. `skip` names a
    // dimension to leave out, which is how each chip row asks "what would
    // be left if my own filter were not applied?" for its counts.
    applyFilters(rows, skip = '') {
      if (this.isAdminView) {
        if (skip !== 'tenant' && this.selectedTenant) rows = rows.filter(t => t.tenant_id === this.selectedTenant)
        if (skip !== 'assignee') {
          if (this.assigneeFilter === this.UNASSIGNED) {
            rows = rows.filter(t => !t.assigned_to)
          } else if (this.assigneeFilter === this.MINE) {
            rows = rows.filter(t => this.inMyScope(t))
          } else if (this.assigneeFilter === this.MY_CREATED) {
            rows = rows.filter(t => this.createdByMe(t))
          } else if (this.assigneeFilter) {
            // a photo chip: that person in any role, the same rule as Mine
            rows = rows.filter(t => ticketInvolves(t, this.assigneeFilter))
          }
        }
        if (skip !== 'createdBy' && this.createdByFilter && this.createdByFilter !== CREATED_BY_SEPARATOR) {
          rows = rows.filter(t => t.username === this.createdByFilter)
        }
        if (skip !== 'vip' && this.vipOnly) rows = rows.filter(t => !!this.tenantVip(t.tenant_id))
        // absent on tickets that predate the flag, hence truthiness
        if (skip !== 'billable' && this.billableOnly) rows = rows.filter(t => !!t.billable)
        if (skip !== 'urgency' && this.urgencyFilters.length) {
          // an unset urgency renders as Low on the list, so it answers to
          // the Low chip too
          rows = rows.filter(t => this.urgencyFilters.includes(t.urgency || 'low'))
        }
      }
      // the closed lanes are bounded by when the ticket was CLOSED --
      // updated_at is the close stamp (update_support_ticket writes it).
      // the window is one setting for both of them.
      rows = rows.filter(t => this.withinClosedWindow(t))
      if (skip !== 'type' && this.typeFilters.length) rows = rows.filter(t => this.typeFilters.includes(t.priority))
      if (skip !== 'feature' && this.categoryFilters.length) rows = rows.filter(t => this.categoryFilters.includes(t.category))
      const needle = this.search.trim().toLowerCase()
      if (needle) rows = rows.filter(t => this.matchesSearch(t, needle))
      return rows
    },
    // the Current chip writes the customer picker, so pressing it again
    // goes back to every customer rather than leaving a second, invisible
    // tenant scope behind the picker's "All customers"
    toggleCurrentTenant() {
      this.selectedTenant = this.currentTenantOnly ? '' : this.currentTenant
    },
    toggleFilter(name, value) {
      const list = this[name]
      this[name] = list.includes(value) ? list.filter(v => v !== value) : [...list, value]
    },
    // the strip's × on one pill: a value leaves its chip set, a scalar
    // goes back to its empty state
    removeFilterPill(pill) {
      // the status pill is a preset: clearing it opens every lane
      if (pill.field === 'statusFilter') {
        this.applyStatusPreset('')
        return
      }
      if (Array.isArray(this[pill.field])) {
        this.toggleFilter(pill.field, pill.value)
      } else if (typeof this[pill.field] === 'boolean') {
        this[pill.field] = false
      } else {
        this[pill.field] = ''
      }
    },
    // the narrowing controls; `everything` also resets the scope (assignee,
    // created by, customer), which the "Clear all" pill strip asks for
    clearFilters(everything = false) {
      if (everything) {
        this.assigneeFilter = ''
        this.createdByFilter = ''
        this.selectedTenant = ''
      }
      this.search = ''
      this.typeFilters = []
      this.urgencyFilters = []
      this.categoryFilters = []
      this.vipOnly = false
      this.billableOnly = false
      this.createdByFilter = ''
    },
    // remembered between visits the way the list remembers its dropdowns.
    // search and the notify checkbox are deliberately not: search is a
    // moment's narrowing, and a remembered "email the customer" would fire
    // a status email from a session that never chose it.
    saveFilters() {
      try {
        localStorage.setItem(FILTERS_KEY, JSON.stringify({
          selectedTenant: this.selectedTenant,
          assigneeFilter: this.assigneeFilter,
          createdByFilter: this.createdByFilter,
          typeFilters: this.typeFilters,
          urgencyFilters: this.urgencyFilters,
          categoryFilters: this.categoryFilters,
          statusFilter: this.statusFilter,
          vipOnly: this.vipOnly,
          billableOnly: this.billableOnly,
          closedWindowDays: this.closedWindowDays,
          collapsedLanes: this.collapsedLanes,
        }))
      } catch (e) { /* private mode: nothing to remember into */ }
    },
    loadFilters() {
      let saved = null
      try {
        saved = JSON.parse(localStorage.getItem(FILTERS_KEY) || 'null')
      } catch (e) {
        saved = null
      }
      if (!saved || typeof saved !== 'object') return
      const laneOk = s => LANES.some(l => l.status === s)
      if (typeof saved.selectedTenant === 'string') this.selectedTenant = saved.selectedTenant
      if (typeof saved.assigneeFilter === 'string') {
        this.assigneeFilter = saved.assigneeFilter
        this.scopeInitialized = true
      }
      if (typeof saved.createdByFilter === 'string') this.createdByFilter = saved.createdByFilter
      if (Array.isArray(saved.typeFilters)) this.typeFilters = saved.typeFilters.filter(v => TYPE_FILTERS.some(o => o.value === v))
      if (Array.isArray(saved.urgencyFilters)) this.urgencyFilters = saved.urgencyFilters.filter(v => URGENCY_FILTERS.some(o => o.value === v))
      const featureOk = v => FEATURE_FILTERS.some(o => o.value === v)
      if (Array.isArray(saved.categoryFilters)) this.categoryFilters = saved.categoryFilters.filter(featureOk)
      // the field was a single-pick select before it became chips
      else if (typeof saved.categoryFilter === 'string' && featureOk(saved.categoryFilter)) this.categoryFilters = [saved.categoryFilter]
      // the chip label only: the folds themselves come back from collapsedLanes
      if (STATUS_FILTERS.some(o => o.value === saved.statusFilter)) this.statusFilter = saved.statusFilter
      this.vipOnly = !!saved.vipOnly
      this.billableOnly = !!saved.billableOnly
      if (CLOSED_WINDOWS.includes(saved.closedWindowDays)) this.closedWindowDays = saved.closedWindowDays
      // the window was a Show-all toggle before it became a picker
      else if (saved.showAllClosed === true) this.closedWindowDays = 0
      if (Array.isArray(saved.collapsedLanes)) this.collapsedLanes = saved.collapsedLanes.filter(laneOk)
    },

    // ---- lanes ----------------------------------------------------------
    // a Status chip: open its lanes, fold the rest. clicking the chip that
    // is already lit re-applies it, which is the way back after folding
    // and opening lanes by hand.
    applyStatusPreset(value) {
      const open = STATUS_PRESETS[value] || STATUS_PRESETS['']
      this.statusFilter = value
      this.collapsedLanes = LANES.map(l => l.status).filter(st => !open.includes(st))
      this.saveFilters()
      this.$nextTick(() => this.syncLaneShadows())
    },
    // what renders as a strip right now: folded, and not opened under a
    // dragged card. isCollapsed is the fold state proper -- the counts and
    // the rank-last rule read that, not the transient hover opening.
    isStrip(status) {
      return this.isCollapsed(status) && this.hoverExpanded !== status
    },
    onLaneKey(col, evt) {
      if (!this.isStrip(col.status)) return
      if (evt.key === 'Enter' || evt.key === ' ') {
        evt.preventDefault()
        this.toggleLane(col.status)
      }
    },
    // the picker is anchored to a card that is about to move, so it closes
    // rather than hanging over the board pointing at nothing
    onDragStart(status) {
      this.closeAssigneePicker()
      this.draggingFrom = status
    },
    // sortablejs move hook, fired as the carried card crosses a container.
    // over a strip: start the clock, and open the lane if the card is
    // still there when it runs out. over anything else: fold back whatever
    // the hover opened, so only the lane under the cursor is open.
    onDragMove(evt) {
      const toEl = evt.to && evt.to.closest ? evt.to.closest('.stb-col') : null
      const status = toEl ? toEl.dataset.status : null
      // a locked lane (Draft) is not a drop target: leaving draft is the
      // send action and only the ticket page does that
      if (status && LOCKED_STATUSES.includes(status)) return false
      if (this._hoverTarget !== status) {
        this._hoverTarget = status
        clearTimeout(this._hoverTimer)
        this._hoverTimer = null
        if (status && this.isCollapsed(status)) {
          this._hoverTimer = setTimeout(() => {
            this._hoverTimer = null
            if (this.draggingFrom && this._hoverTarget === status) {
              this.hoverExpanded = status
              this.$nextTick(() => this.syncLaneShadows())
            }
          }, HOVER_OPEN_MS)
        } else if (this.hoverExpanded && this.hoverExpanded !== status) {
          this.hoverExpanded = null
        }
      }
      return true
    },
    onDragEnd(evt) {
      clearTimeout(this._hoverTimer)
      this._hoverTimer = null
      this._hoverTarget = null
      // a card dropped into a lane the hover opened keeps that lane open:
      // the viewer just put something there and will want to see it land
      const toEl = evt && evt.to && evt.to.closest ? evt.to.closest('.stb-col') : null
      const landed = toEl ? toEl.dataset.status : null
      if (this.hoverExpanded && landed === this.hoverExpanded) {
        this.collapsedLanes = this.collapsedLanes.filter(st => st !== landed)
      }
      this.hoverExpanded = null
      this.draggingFrom = null
    },
    // the fold state proper. any lane, any time -- the Status chips only
    // set this in bulk.
    isCollapsed(status) {
      return this.collapsedLanes.includes(status)
    },
    stripTitle(col) {
      return `Expand ${col.label}`
    },
    toggleLane(status) {
      this.collapsedLanes = this.isCollapsed(status)
        ? this.collapsedLanes.filter(st => st !== status)
        : [...this.collapsedLanes, status]
      // the lanes re-measure after the fold
      this.$nextTick(() => this.syncLaneShadows())
    },
    laneEmptyText(col) {
      if (this.isFiltered || (this.isAdminView && this.assigneeFilter)) return 'Nothing here matches the filters'
      return col.empty
    },
    // what this lane is to the card currently in flight
    dropState(status) {
      if (!this.draggingFrom) return ''
      return status === this.draggingFrom ? 'source' : 'target'
    },
    dropStateClass(status) {
      const state = this.dropState(status)
      return state ? 'stb-col--drop-' + state : ''
    },
    noteStateClass(status) {
      return this.dropState(status) === 'target' ? 'stb-col-note--target' : ''
    },
    laneNote(col) {
      if (this.dropState(col.status) === 'target') return DROP_NOTES[col.status]
      if (this.dropState(col.status) === 'source' && !col.closed && this.canDrag) return 'drop back here to re-rank'
      return col.note
    },
    resetColumns() {
      const cols = {}
      LANES.forEach(l => { cols[l.status] = [] })
      this.columns = cols
    },
    rebuildColumns() {
      const cols = {}
      LANES.forEach(l => { cols[l.status] = [] })
      this.scopedTickets.forEach(t => {
        const lane = laneStatus(t.ticket_status)
        if (cols[lane]) cols[lane].push(t)
      })
      LANES.forEach(l => cols[l.status].sort(l.closed ? closedSort : openSort))
      this.columns = cols
    },
    flashCard(key, kind) {
      this.$set(this.flashes, key, kind)
      const timer = setTimeout(() => {
        this.$delete(this.flashes, key)
        this.flashTimers = this.flashTimers.filter(t => t !== timer)
      }, FLASH_MS)
      this.flashTimers.push(timer)
    },

    // ---- data -----------------------------------------------------------
    async loadTickets() {
      this.loading = true
      this.error = null
      // a tenant switch mid-flight replaces the dataset this load belongs to
      this._loadGen = (this._loadGen || 0) + 1
      const gen = this._loadGen
      try {
        // every status, every date: the lanes are the status filter and
        // the closed window is applied client-side. the api's tenant
        // clause is what scopes a non-staff viewer to their own tickets.
        const { value: { data = [] } = {} } = await search_support_tickets(
          this.queryTenant, '', '', '', '', '', '', '', '',
        ) || {}
        if (this._loadGen !== gen) return
        this.tickets = Array.isArray(data) ? data : []
        // a first visit opens on the viewer's own tickets
        if (!this.scopeInitialized) {
          this.assigneeFilter = this.isAdminView ? this.MINE : ''
          this.scopeInitialized = true
        }
        this.rebuildColumns()
      } catch (e) {
        if (this._loadGen !== gen) return
        this.error = this.apiErrorMessage(e, 'Failed to load the ticket board.')
      } finally {
        if (this._loadGen === gen) this.loading = false
      }
    },
    // ---- invoice prep: monthly billable hours (staff) ----
    openBillableSummary() {
      if (!this.isAdminView) return
      // invoices go out after month close, so open on the month just ended
      if (!this.billableMonth) this.billableMonth = moment().subtract(1, 'month').format('YYYY-MM')
      this.$bvModal.show('stb-billable-modal')
      this.loadBillableSummary()
    },
    async loadBillableSummary() {
      const month = this.billableMonth
      const assignee = this.billableAssignee
      if (!month) return
      this.billableLoading = true
      this.billableError = null
      this.billableCopied = false
      // the pickers may move on while this is in flight; a stale answer is dropped
      const current = () => month === this.billableMonth && assignee === this.billableAssignee
      try {
        const result = await get_support_ticket_billable_summary(month, assignee)
        if (!current()) return
        this.billableSummary = (result && result.value && result.value.data) || null
      } catch (e) {
        if (!current()) return
        this.billableSummary = null
        this.billableError = 'could not build the summary: ' + ((e && e.message) || e)
      } finally {
        if (current()) this.billableLoading = false
      }
    },
    async copyBillableSummary() {
      const text = this.billableText
      if (!text) return
      let copied = false
      try {
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(text)
          copied = true
        }
      } catch (e) {
        copied = false
      }
      if (!copied) {
        // legacy path for browsers that refuse the async clipboard here
        const area = document.createElement('textarea')
        area.value = text
        area.setAttribute('readonly', '')
        area.style.position = 'fixed'
        area.style.left = '-9999px'
        document.body.appendChild(area)
        area.select()
        try { copied = document.execCommand('copy') } catch (e) { copied = false }
        document.body.removeChild(area)
      }
      if (copied) {
        this.billableCopied = true
        setTimeout(() => { this.billableCopied = false }, 2000)
      } else {
        this.$emit('showAlert', 'could not write to clipboard', 'warning', 4000)
      }
    },
    reload() {
      return this.loadTickets()
    },

    // ---- the drag -------------------------------------------------------
    // vuedraggable's change event: `moved` is a reorder inside this lane,
    // `added` is a card arriving from another lane (whose own `removed`
    // event is ignored -- the arrival is the whole story).
    async onColumnChange(status, evt) {
      if (!this.canDrag) return
      if (evt.moved) {
        // a ticket still on a retired status gets written back as the lane
        // it shows in, so the stored status and the rank agree
        const t = evt.moved.element
        if (laneStatus(t.ticket_status) !== t.ticket_status) {
          await this.moveTicket(t, status)
          return
        }
        await this.persistLaneRanks(status)
        return
      }
      if (!evt.added) return
      await this.moveTicket(evt.added.element, status)
    },
    // a status change is the list's inline edit, made with a drop: read the
    // stored record, change the one field, put it back. silent for the
    // customer unless the viewer opted in. then the destination lane is
    // ranked so the card stays exactly where it was dropped.
    async moveTicket(ticket, toStatus) {
      const key = this.ticketKey(ticket)
      const lane = LANES.find(l => l.status === toStatus)
      // read before the first await: the drag's end clears the hover state
      const droppedOnStrip = this.isStrip(toStatus)
      this.saving = true
      try {
        const { value: { data = {} } = {} } = await get_support_ticket(ticket.tenant_id, ticket.id) || {}
        if (!data.item) throw new Error('ticket not found')
        const model = { ...data.item, new_tenant_id: data.item.tenant_id, ticket_status: toStatus }
        const resp = await update_support_ticket(ticket.tenant_id, model, this.notifyOnMove ? 'true' : 'false')
        const result = resp && resp.value && resp.value.data
        // update_support_ticket reports a server-side failure inside a 200
        if (result && result.status === 'error') throw new Error(result.message || 'update failed')
        const i = this.tickets.findIndex(t => this.ticketKey(t) === key)
        if (i !== -1) {
          this.$set(this.tickets, i, {
            ...this.tickets[i], ...data.item,
            ticket_status: toStatus,
            updated_at: moment.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
            updated_by: this.user && this.user.username,
          })
        }
        this.flashCard(key, 'landed')
        this.toast(`Ticket #${ticket.id} moved to ${lane ? lane.label : toStatus}.`, 'info')
        if (lane && !lane.closed) {
          // a strip has no positions to drop between: the card ranks last.
          // a lane the hover opened has, so the drop position stands.
          if (droppedOnStrip) {
            this.rebuildColumns()
            const col = this.columns[toStatus]
            const i = col.findIndex(t => this.ticketKey(t) === key)
            if (i !== -1) col.push(col.splice(i, 1)[0])
          }
          await this.persistLaneRanks(toStatus)
        }
      } catch (e) {
        this.toast(
          this.apiErrorMessage(e, `Could not move ticket #${ticket.id} to ${lane ? lane.label : toStatus}.`),
          'danger', 6000,
        )
        // the drag moved the card between the lane arrays but the ticket's
        // own status never changed, so the rebuild snaps it back
        this.flashCard(key, 'rejected')
      } finally {
        this.saving = false
        this.rebuildColumns()
      }
    },
    // ---- reassign from the card ----------------------------------------
    // a click on the face opens the roster over the board. for a non-admin
    // the face is a plain span with no picker, so the click falls through to
    // the card and opens the ticket, as it always has.
    onAssigneeClick(t, ev) {
      if (!this.isAdminView) return
      // the card's own click opens the ticket; this one must not
      ev.stopPropagation()
      this.openAssigneePicker(t, ev)
    },
    openAssigneePicker(t, ev) {
      const key = this.ticketKey(t)
      // the same face again closes it, the way the vsm chips toggle
      if (this.assigneePickerKey === key) { this.closeAssigneePicker(true); return }
      const trigger = ev && (ev.currentTarget || ev.target)
      this._assigneeRect = (trigger && trigger.getBoundingClientRect) ? trigger.getBoundingClientRect() : null
      this.assigneePickerTicket = t
      this.assigneePickerKey = key
      // off-screen first, so the unpositioned popover never flashes
      this.assigneePickerStyle = { top: '-9999px', left: '-9999px' }
      this.$nextTick(() => {
        this.positionAssigneePicker()
        document.addEventListener('mousedown', this.onAssigneeDocClick, true)
        // fixed and anchored to a card that scrolls: any scroll (the window's
        // or a lane's own scrollport) would detach it, so it closes instead
        window.addEventListener('scroll', this.closeAssigneePicker, true)
        window.addEventListener('resize', this.closeAssigneePicker, true)
        const pop = this.$refs.assigneePop
        const el = pop && (pop.querySelector('.stb-assign-opt--current') || pop.querySelector('.stb-assign-opt'))
        if (el && el.focus) el.focus()
      })
    },
    // under the face by default, flipped above when the lane bottom is close;
    // right-aligned on it (the face sits at the card's right edge), clamped
    // to the viewport either way
    positionAssigneePicker() {
      const rect = this._assigneeRect
      const pop = this.$refs.assigneePop
      if (!rect || !pop) return
      const margin = 8
      const gap = 6
      const pw = pop.offsetWidth
      const ph = pop.offsetHeight
      let top = rect.bottom + gap
      if (top + ph > window.innerHeight - margin) {
        const above = rect.top - gap - ph
        top = above >= margin ? above : Math.max(margin, window.innerHeight - margin - ph)
      }
      let left = rect.right - pw
      if (left + pw > window.innerWidth - margin) left = window.innerWidth - margin - pw
      if (left < margin) left = margin
      this.assigneePickerStyle = { top: Math.round(top) + 'px', left: Math.round(left) + 'px' }
    },
    // restoreFocus puts focus back on the face that opened the picker so
    // keyboard users keep their place; an outside click leaves focus alone.
    // also the scroll/resize handler, which passes an event -- hence the
    // explicit true rather than a truthy argument.
    closeAssigneePicker(restoreFocus) {
      const key = this.assigneePickerKey
      this.assigneePickerTicket = null
      this.assigneePickerKey = null
      this.assigneePickerStyle = null
      this._assigneeRect = null
      document.removeEventListener('mousedown', this.onAssigneeDocClick, true)
      window.removeEventListener('scroll', this.closeAssigneePicker, true)
      window.removeEventListener('resize', this.closeAssigneePicker, true)
      if (restoreFocus === true && key) {
        this.$nextTick(() => {
          const el = this.$el.querySelector(`[data-assignee-trigger="${key}"]`)
          if (el && el.focus) el.focus()
        })
      }
    },
    // a pointer-down outside the popover closes it; one on another card's
    // face just re-targets the picker there
    onAssigneeDocClick(e) {
      if (e.target.closest && (e.target.closest('.stb-assign-pop') || e.target.closest('.stb-assignee-wrap--btn'))) return
      this.closeAssigneePicker()
    },
    pickAssignee(username) {
      const t = this.assigneePickerTicket
      this.closeAssigneePicker(true)
      if (!t) return
      this.assignTicket(t, username || null)
    },
    // the assignment write: the list's inline Assigned edit, made from the
    // card. read the stored record, change the one field, put it back. the
    // customer is never emailed -- assignment is internal triage -- and the
    // server posts the change to Slack when it differs from what was stored.
    // the card's lane and rank are untouched.
    async assignTicket(ticket, username) {
      const key = this.ticketKey(ticket)
      const who = username ? this.displayUserName(username) : 'nobody'
      if ((ticket.assigned_to || null) === (username || null)) return
      // a second pick on the same card while the first is in the air would
      // race the two writes against each other
      if (this.assigning[key]) return
      this.$set(this.assigning, key, true)
      try {
        const { value: { data = {} } = {} } = await get_support_ticket(ticket.tenant_id, ticket.id) || {}
        if (!data.item) throw new Error('ticket not found')
        const model = { ...data.item, new_tenant_id: data.item.tenant_id, assigned_to: username }
        const resp = await update_support_ticket(ticket.tenant_id, model, 'false')
        const result = resp && resp.value && resp.value.data
        // update_support_ticket reports a server-side failure inside a 200
        if (result && result.status === 'error') throw new Error(result.message || 'update failed')
        const i = this.tickets.findIndex(t => this.ticketKey(t) === key)
        if (i !== -1) {
          this.$set(this.tickets, i, {
            ...this.tickets[i], ...data.item,
            assigned_to: username,
            updated_at: moment.utc().format('YYYY-MM-DDTHH:mm:ss[Z]'),
            updated_by: this.user && this.user.username,
          })
        }
        this.flashCard(key, 'landed')
        // under a narrowed scope the card leaves the board on the way out,
        // so the toast says where it went rather than just that it saved
        this.toast(username
          ? `Ticket #${ticket.id} assigned to ${who}.`
          : `Ticket #${ticket.id} unassigned.`, 'info')
      } catch (e) {
        this.toast(
          this.apiErrorMessage(e, username
            ? `Could not assign ticket #${ticket.id} to ${who}.`
            : `Could not unassign ticket #${ticket.id}.`),
          'danger', 6000,
        )
        this.flashCard(key, 'rejected')
      } finally {
        this.$delete(this.assigning, key)
        this.rebuildColumns()
      }
    },

    // persist one lane's order as queue_rank 1..n over the FULL lane (see
    // mergeLaneOrder), sending only the rows whose rank actually changed.
    async persistLaneRanks(status) {
      if (!this.canDrag) return
      const visible = this.columns[status] || []
      const full = this.tickets.filter(t => laneStatus(t.ticket_status) === status).slice().sort(openSort)
      const merged = mergeLaneOrder(full, visible, this.ticketKey)
      const items = []
      merged.forEach((t, i) => {
        if (rankOf(t) !== i + 1) items.push({ tenant_id: t.tenant_id, id: t.id, queue_rank: i + 1 })
      })
      if (!items.length) return
      this.ranking = true
      try {
        const { value: { data = {} } = {} } = await rank_support_tickets({ items }) || {}
        const byKey = new Map(items.map(it => [`${it.tenant_id}#${it.id}`, it.queue_rank]))
        this.tickets = this.tickets.map(t => byKey.has(this.ticketKey(t))
          ? { ...t, queue_rank: byKey.get(this.ticketKey(t)) } : t)
        // a row that vanished between load and drop is reported, not
        // written; the next reload drops it from the board
        if (data && data.updated !== undefined && data.updated < data.requested) {
          this.toast('Some tickets were not ranked — they may have been deleted. Reload to refresh the board.', 'warning', 6000)
        }
      } catch (e) {
        this.toast(this.apiErrorMessage(e, 'Could not save the new order.'), 'danger', 6000)
      } finally {
        this.ranking = false
        this.rebuildColumns()
      }
    },

    // ---- the ticket modal ----------------------------------------------
    async onTicketCreated(created) {
      await this.loadTickets()
      if (created && created.attachmentFailures > 0) this.openTicket(created)
    },
    openTicket(t, { fromRoute = false } = {}) {
      // the url names the open ticket so the modal survives a refresh and
      // the link can be shared; replace, not push, so Back leaves the page
      // rather than stepping through every card that was opened
      if (!fromRoute) {
        const query = { ...this.$route.query, ticket_id: String(t.id), tenant_id: t.tenant_id }
        this.$router.replace({ query }).catch(() => {})
      }
      this.$refs.ticketModal.open(t)
    },
    // whatever happened in the modal (a status change, a comment, a
    // cancel, an assignment) is on the server now: refetch that one ticket
    // and patch the card rather than rescanning the whole board
    async onTicketModalHidden(ref) {
      if (this.$route.query.ticket_id) {
        const query = { ...this.$route.query }
        delete query.ticket_id
        delete query.tenant_id
        this.$router.replace({ query }).catch(() => {})
      }
      if (!ref) return
      try {
        const { value: { data = {} } = {} } = await get_support_ticket(ref.tenant_id, ref.id) || {}
        const item = data.item
        const key = `${ref.tenant_id}#${ref.id}`
        const i = this.tickets.findIndex(t => this.ticketKey(t) === key)
        if (!item) {
          // gone (deleted, or moved to another account): drop the card
          if (i !== -1) this.tickets.splice(i, 1)
        } else if (i !== -1) {
          this.$set(this.tickets, i, { ...this.tickets[i], ...item })
        } else if (item.ticket_status) {
          // opened by deep link before the board knew it (a fresh ticket,
          // or a filter the api applies that the board has not)
          this.tickets.push(item)
        }
        this.rebuildColumns()
      } catch (e) {
        // the board is stale at worst; the refresh button is one click away
      }
    },
  },
}
</script>

<style scoped>
.stb-root {
  max-width: none;
  padding: 12px 20px 2px 20px;
  text-align: left;
  font-size: 14px;
}
/* the rail bleeds into the page gutters so its title lines up with the
   lanes below (the erm board's .eb-root treatment) */
@media (min-width: 601px) {
  .stb-root ::v-deep .fi-pr-rail { margin: -11px -19px 14px -19px; }
}
.stb-stale { opacity: 0.6; }
.stb-small { font-size: 0.85em; color: var(--fi-text-muted, #6b7280); }
.stb-scope-row { display: flex; align-items: center; gap: 10px 14px; margin: 12px 0 14px; flex-wrap: wrap; }
.stb-mobile-only { display: none; }
.stb-filter-toggle {
  align-items: center; gap: 7px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 7px 14px; margin: 14px 0 10px;
  font-size: 13.5px; font-weight: 600;
  color: var(--fi-text, #374151);
  cursor: pointer; transition: background .12s, border-color .12s;
}
.stb-filter-toggle:hover { background: var(--fi-surface-sunken, #eef4fb); }
.stb-filter-toggle:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
.stb-filter-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  font-size: 11px; font-weight: 700; line-height: 1;
}
.stb-tenant-select { max-width: 240px; font-size: 0.9em; padding-top: 3px; padding-bottom: 3px; height: auto; }
.stb-scope-select { max-width: 220px; font-size: 0.9em; padding-top: 3px; padding-bottom: 3px; height: auto; }
.stb-filter-select { max-width: 160px; font-size: 0.85em; text-overflow: ellipsis; }
.stb-check { font-size: 13px; font-weight: 600; color: var(--fi-text-muted, #4b5563); }
.stb-scope-selects { display: inline-flex; align-items: center; gap: 6px; margin-left: auto; }

/* search pill (the erm board's) */
.stb-search {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 3px 10px;
  background: transparent;
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  min-width: 220px;
}
.stb-search:focus-within { border-color: var(--fi-ops-accent, #3a6e99); }
.stb-search-icon { font-size: 11px; color: var(--fi-text-muted, #8a94a1); }
.stb-search-input {
  flex: 1 1 auto; min-width: 0;
  background: transparent;
  border: 0; outline: none;
  font-size: 13px;
  color: var(--fi-text, #2c2c2a);
}
.stb-search-input::placeholder { color: var(--fi-text-faint, #9aa4b1); }
.stb-search-clear {
  background: none; border: 0; padding: 0;
  font-size: 11px; line-height: 1;
  color: var(--fi-text-muted, #8a94a1);
  cursor: pointer;
}
.stb-search-clear:hover { color: var(--fi-ops-accent, #3a6e99); }
.stb-no-matches { font-size: 12.5px; color: var(--fi-text-muted, #6b7280); margin: 0 0 10px; }
.stb-link-btn {
  background: none; border: 0; padding: 0;
  font-size: inherit;
  color: var(--fi-ops-accent, #3a6e99);
  text-decoration: underline;
  cursor: pointer;
}
.stb-filter-row {
  display: flex; align-items: center; flex-wrap: wrap;
  gap: 10px 16px;
  margin: 0 0 14px;
}
.stb-chip {
  background: var(--fi-surface, #f8fafc);
  border: 1px solid var(--fi-line, #d6dde6);
  border-radius: var(--fi-radius-pill, 999px);
  padding: 3px 9px;
  font-size: 11.5px;
  line-height: 1.5;
  font-weight: 600;
  color: var(--fi-text-muted, #4b5563);
  cursor: pointer;
  transition: background .12s, border-color .12s, color .12s;
}
.stb-chip:hover { background: var(--fi-surface-sunken, #eef4fb); }
/* a pressed plain chip keeps its fill on hover: the generic hover above
   outranks .stb-chip--active alone and was swapping the blue for the
   light surface under white text */
.stb-chip.stb-chip--active:hover {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
  filter: brightness(1.08);
}
.stb-chip--active {
  background: var(--fi-ops-accent, #3a6e99);
  border-color: var(--fi-ops-accent, #3a6e99);
  color: #fff;
}
/* a pressed chip also gets a check and a ring, so it stands out from its
   unpressed neighbours whatever colour it wears */
.stb-chip--active::before {
  content: '\f00c'; font-family: FontAwesome; font-size: 8.5px;
  margin-right: 4px;
}
.stb-chip.stb-chip--active { box-shadow: 0 0 0 2px var(--fi-surface, #fff), 0 0 0 3.5px currentColor; }
/* the Status presets are a view setting, not a narrowing: no check or ring */
.stb-group[aria-label="Status"] .stb-chip--active::before { content: none; }
.stb-group[aria-label="Status"] .stb-chip.stb-chip--active { box-shadow: none; }
/* the pressed photo chip gets the same ring */
.stb-team-chip--selected { box-shadow: 0 0 0 1px var(--fi-surface, #fff), 0 0 0 3px var(--fi-ops-accent, #3a6e99); }

/* the "Filtering by" strip: one removable pill per active filter */
.stb-active {
  display: flex; align-items: center; flex-wrap: wrap; gap: 5px 6px;
  margin: 0 0 10px; padding: 5px 9px;
  border: 1px solid rgba(58, 110, 153, 0.35); border-left: 3px solid var(--fi-ops-accent, #3a6e99);
  border-radius: 6px;
  background: rgba(58, 110, 153, 0.07);
  font-size: 11.5px;
}
.stb-active-label { font-weight: 700; color: var(--fi-ops-accent, #3a6e99); margin-right: 2px; }
.stb-active-label .fa { font-size: 10px; margin-right: 2px; }
.stb-active-pill {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 1px 7px 1px 8px;
  border: 1px solid var(--fi-ops-accent, #3a6e99); border-radius: 999px;
  background: var(--fi-surface, #fff); color: var(--fi-text, #374151);
  font-size: 11px; font-weight: 600; cursor: pointer;
}
.stb-active-pill:hover { background: rgba(58, 110, 153, 0.12); }
.stb-active-pill .fa { font-size: 9px; color: var(--fi-text-muted, #8a94a1); }
.stb-active-pill:hover .fa { color: #b91c1c; }
.stb-active-dim { font-weight: 400; color: var(--fi-text-muted, #6b7280); }
.stb-active-clear { margin-left: auto; font-size: 11px; }
/* the count rides the chip as a quiet numeral; on an active chip it goes
   white with the label */
.stb-chip-count {
  margin-left: 3px;
  font-weight: 700; font-variant-numeric: tabular-nums;
  opacity: 0.7;
}
/* the fold toggle on the Feature row: quieter than a value chip */
.stb-chip--more { font-weight: 600; opacity: 0.8; }
.stb-chip--more .fa { font-size: 10px; margin-right: 3px; }
.stb-chip--active .stb-chip-count { opacity: 0.9; }
.stb-chip--tone .fa { font-size: 10px; margin-right: 4px; }
/* a toned chip wears its tag's palette at rest (the stb-tag-* rules)
   and fills solid with that palette's border colour when active */
.stb-chip--tone.stb-chip--active.stb-tag-info    { background: #4a7ba7; border-color: #4a7ba7; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-tag-warning { background: #b58100; border-color: #b58100; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-tag-danger  { background: #dc3545; border-color: #dc3545; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-tag-success { background: #3a8144; border-color: #3a8144; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-tag-orange  { background: #ff6d00; border-color: #ff6d00; color: #fff; }
.stb-chip--tone:hover { filter: brightness(0.96); }
/* the team strip (support_ticket_list's .stl-dev-chip recipe): circles
   with a transparent ring that fills on hover / selection */
.stb-team-chips { display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; margin-right: 4px; }
.stb-team-chip {
  display: inline-flex; align-items: center; justify-content: center;
  width: 32px; height: 32px; padding: 0;
  border: 2px solid transparent; border-radius: 50%;
  background: transparent; cursor: pointer;
  transition: border-color 0.15s, box-shadow 0.15s, transform 0.15s;
}
.stb-team-chip:hover { border-color: #6c757d; transform: translateY(-1px); }
.stb-team-chip--selected {
  border-color: var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 0 0 1px rgba(58, 110, 153, 0.35);
}
.stb-team-chip { position: relative; }
.stb-team-chip-img { width: 28px; height: 28px; border-radius: 50%; object-fit: cover; display: block; }
/* the count sits on the photo's lower-right shoulder */
.stb-team-chip-count {
  position: absolute; right: -5px; bottom: -4px;
  min-width: 15px; height: 15px; padding: 0 4px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
  border: 1.5px solid var(--fi-surface, #fff);
  font-size: 9.5px; font-weight: 700; line-height: 12px;
  font-variant-numeric: tabular-nums; text-align: center;
  pointer-events: none;
}
.stb-team-chip-icon {
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
  width: 28px; height: 28px; border-radius: 50%;
  background: #eef0f2; color: #666;
  font-size: 14px; line-height: 1;
}
.stb-team-chip-icon--unassigned::after {
  content: ''; position: absolute;
  left: 3px; right: 3px; top: 50%; height: 2px; margin-top: -1px;
  background: #c0392b; transform: rotate(-45deg); border-radius: 1px;
  pointer-events: none;
}
.stb-group { display: inline-flex; align-items: center; gap: 4px; flex-wrap: wrap; }
/* a hairline between chip sections, with air on both sides, so Status |
   Type | Feature read as separate controls rather than one long run.
   the first section in a row carries none. */
.stb-group:not(:first-child) {
  border-left: 1px solid var(--fi-line-strong, #d6dde6);
  padding-left: 18px;
  margin-left: 8px;
}
.stb-group-label {
  font-size: 11.5px; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.4px;
  color: var(--fi-text-muted, #6b7280);
  margin-right: 4px;
}

/* the board: a viewport-bound surface (FI-2113): it fills the space under
   the chrome above it (measured into --sl-board-top by the shared mixin)
   and each lane scrolls itself. ten lanes scroll sideways. */
.stb-board {
  display: flex; gap: 12px; align-items: stretch; overflow-x: auto;
  /* vh, NOT dvh -- a dvh calc breaks the build's css minifier (see erm_board) */
  height: calc(100vh - var(--sl-board-top, 320px) - 6px - var(--sl-board-shave, 0px));
  min-height: 320px;
  padding-bottom: 2px;
}
@media (min-width: 601px) {
  .fi-hz-content .stb-board,
  .fi-hz-content .stb-scope-row,
  .fi-hz-content .stb-filter-row,
  .fi-hz-content .stb-active,
  .fi-hz-content .stb-no-matches { margin-left: -19px; margin-right: -19px; }
}
.fi-hz-content .stb-root { margin-bottom: -19px; }
@media (min-width: 601px) and (max-width: 768px) {
  .fi-hz-content .stb-board,
  .fi-hz-content .stb-scope-row,
  .fi-hz-content .stb-filter-row,
  .fi-hz-content .stb-active,
  .fi-hz-content .stb-no-matches { margin-left: -7px; margin-right: -7px; }
}
.stb-col {
  flex: 0 0 290px; min-width: 290px; min-height: 0;
  /* a strip opening under a dragged card grows rather than pops */
  transition: flex-basis .15s ease, min-width .15s ease;
  display: flex; flex-direction: column;
  background: var(--fi-surface-sunken, #f4f6f8);
  border: 1px solid var(--fi-line, #dee2e6);
  border-radius: 8px;
  padding: 8px;
}
/* the All preset's narrower lanes. folded strips keep their 38px, and
   phones run one lane per screen (the 600px block at the foot), so this
   starts above the phone width. */
@media (min-width: 601px) {
  .stb-board--dense .stb-col:not(.stb-col--collapsed) { flex: 0 0 248px; min-width: 248px; }
}
/* a folded lane: a strip with the count and the name read upward */
.stb-col--collapsed {
  flex: 0 0 38px; min-width: 38px;
  align-items: center;
  padding: 8px 4px;
  cursor: pointer;
  gap: 8px;
}
.stb-col--collapsed:hover { border-color: var(--fi-ops-accent, #3a6e99); }
.stb-col--collapsed:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }
.stb-col--collapsed { position: relative; }
/* on a strip the lane's scroll area becomes an invisible overlay over the
   whole strip, so the draggable inside it (the same container as when the
   lane is open) takes a drop anywhere on the strip. the cards it holds,
   and the ghost sortablejs parks there while a card hovers, stay hidden:
   the strip's look holds and the highlight speaks. */
.stb-col--collapsed .stb-col-scroll {
  position: absolute; left: 0; right: 0; top: 0; bottom: 0;
  margin: 0; padding: 0; overflow: hidden;
}
.stb-col--collapsed .stb-col-scroll::before,
.stb-col--collapsed .stb-col-scroll::after { display: none; }
.stb-col--collapsed .stb-col-body { min-height: 100%; }
.stb-col--collapsed .stb-card,
.stb-col--collapsed .stb-col-empty { display: none; }
.stb-col--collapsed.stb-col--drop-target { background: rgba(58, 110, 153, 0.18); }
.stb-col-collapsed-icon { font-size: 18px; color: var(--fi-text-muted, #8a94a1); }
.stb-col-collapsed-count {
  font-size: 12px; font-weight: 700;
  color: var(--fi-text-muted, #6b7280);
  font-variant-numeric: tabular-nums;
}
.stb-col-collapsed-label {
  writing-mode: vertical-rl; transform: rotate(180deg);
  font-size: 11px; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  color: var(--fi-text-muted, #4b5563);
  white-space: nowrap;
}
.stb-col-scroll {
  position: relative; flex: 1 1 auto; min-height: 0;
  display: flex; flex-direction: column;
  overflow-y: auto;
  margin: 0 -4px; padding: 0 4px;
}
.stb-col-scroll::before,
.stb-col-scroll::after {
  content: ''; display: block; flex: 0 0 0px; height: 0;
  position: sticky; z-index: 2; pointer-events: none;
  opacity: 0; transition: opacity .15s;
}
.stb-col-scroll::before { top: 0; order: -1; box-shadow: 0 7px 9px -6px rgba(15, 23, 42, 0.35); }
.stb-col-scroll::after { bottom: 0; box-shadow: 0 -7px 9px -6px rgba(15, 23, 42, 0.35); }
.stb-col-scroll--shadow-top::before { opacity: 1; }
.stb-col-scroll--shadow-bottom::after { opacity: 1; }
[data-fi-theme="midnight"] .stb-col-scroll::before { box-shadow: 0 7px 10px -6px rgba(0, 0, 0, 0.8); }
[data-fi-theme="midnight"] .stb-col-scroll::after { box-shadow: 0 -7px 10px -6px rgba(0, 0, 0, 0.8); }
.stb-col-empty {
  position: absolute; left: 6px; right: 6px; top: 6px; bottom: 6px;
  display: flex; align-items: center; justify-content: center;
  text-align: center; padding: 8px;
  font-size: 12px; color: var(--fi-text-muted, #8a94a1);
  border: 1px dashed var(--fi-line-strong, #d6dde6); border-radius: 6px;
  pointer-events: none;
}
.stb-col-head { display: flex; align-items: center; justify-content: space-between; gap: 6px; padding: 2px 2px 0; }
.stb-col-head-right { display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0; }
.stb-col-count { font-size: 12px; font-weight: 700; color: var(--fi-text-muted, #6b7280); font-variant-numeric: tabular-nums; }
.stb-col-fold {
  background: none; border: 0; padding: 0 3px;
  font-size: 18px; line-height: 1;
  color: var(--fi-text-faint, #9aa4b1);
  cursor: pointer;
}
.stb-col-fold:hover { color: var(--fi-ops-accent, #3a6e99); }
.stb-col-note {
  display: flex; align-items: center; gap: 6px; flex-wrap: wrap;
  font-size: 11px; color: var(--fi-text-muted, #8a94a1); padding: 2px 2px 6px;
}
/* the closed lanes' window picker, at the note's right edge: the chosen
   span is the one in ink, the rest are a click away */
.stb-col-window { display: inline-flex; align-items: center; gap: 2px; margin-left: auto; }
.stb-col-window-btn {
  background: none; border: 0; padding: 0 4px;
  font-size: 11px; line-height: 1.5; font-weight: 600;
  color: var(--fi-ops-accent, #3a6e99);
  border-radius: 3px; cursor: pointer;
  transition: background .12s, color .12s;
}
.stb-col-window-btn:hover { background: var(--fi-surface-sunken, #eef4fb); }
.stb-col-window-btn--active { color: var(--fi-text, #374151); background: var(--fi-surface-sunken, #eef4fb); cursor: default; }
.stb-col-window-btn:focus-visible { outline: 2px solid var(--fi-ops-accent, #3a6e99); outline-offset: 1px; }
.stb-col-body { flex: 1 0 auto; min-height: 60px; display: flex; flex-direction: column; gap: 6px; }

/* lane top accents follow the status chip families */
.stb-col--draft     { box-shadow: inset 0 3px 0 0 #d9c4a3; }
.stb-col--new       { box-shadow: inset 0 3px 0 0 #9aa4b1; }
.stb-col--queued    { box-shadow: inset 0 3px 0 0 #b8c4d6; }
.stb-col--review    { box-shadow: inset 0 3px 0 0 #c7b3ea; }
.stb-col--progress  { box-shadow: inset 0 3px 0 0 #8db8f2; }
.stb-col--waiting   { box-shadow: inset 0 3px 0 0 #f0d97a; }
.stb-col--testing   { box-shadow: inset 0 3px 0 0 #a9b6f0; }
.stb-col--blocked   { box-shadow: inset 0 3px 0 0 #f2a4a4; }
.stb-col--completed { box-shadow: inset 0 3px 0 0 #7bcf99; }
.stb-col--cancelled { box-shadow: inset 0 3px 0 0 #cfd4da; }

/* a card is a drag handle and a tap target, never text to select: the
   fallback drag sweeps the pointer across cards and the browser would
   otherwise paint a selection over the card and everything it shoves
   aside. the root rule below covers the rest of the board mid-drag. */
.stb-card { user-select: none; -webkit-user-select: none; }
.stb-card img { -webkit-user-drag: none; }
.stb-root--dragging, .stb-root--dragging * { user-select: none !important; -webkit-user-select: none !important; }
.stb-card {
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line-strong, #dfe4ea);
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 14px;
  cursor: grab;
  box-shadow: 0 1px 1px rgba(15, 23, 42, 0.04);
  transition: border-color .12s, box-shadow .12s;
}
.stb-card:hover { border-color: var(--fi-ops-accent, #3a6e99); }
.stb-card--closed { opacity: 0.82; }
.stb-card--locked { cursor: pointer; }
/* flagged cards carry the house rail edge: square 3px accent, soft right */
.stb-card--stale,
.stb-card--major { border-radius: 0 6px 6px 0; }
.stb-card--stale { border-left: 3px solid #e8912d; }
.stb-card--major { border-left: 3px solid #d62f2f; }
.stb-card--stale:hover { border-left-color: #e8912d; }
.stb-card--major:hover { border-left-color: #d62f2f; }

/* the drag, made legible (the erm board's recipe): the slot left behind
   goes hollow, the card in flight lifts, the other lanes say they will
   take it, and the card pulses once where it comes to rest */
.stb-card--ghost {
  opacity: 1;
  background: rgba(58, 110, 153, 0.12);
  border: 2px dashed var(--fi-ops-accent, #3a6e99);
  box-shadow: none;
}
.stb-card--ghost > * { visibility: hidden; }
.stb-card--dragging {
  cursor: grabbing;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-ops-accent, #3a6e99);
  box-shadow: 0 14px 30px rgba(15, 23, 42, 0.30);
}
.stb-col--drop-target {
  background: rgba(58, 110, 153, 0.10);
  outline: 2px solid var(--fi-ops-accent, #3a6e99);
  outline-offset: -1px;
}
.stb-col--drop-target .stb-col-body {
  box-shadow: inset 0 0 0 1px rgba(58, 110, 153, 0.35);
  border-radius: 6px;
}
.stb-col-note--target { color: var(--fi-ops-accent, #3a6e99); font-weight: 700; }
@keyframes stb-pulse-landed {
  0%   { box-shadow: 0 0 0 4px rgba(22, 101, 52, 0.45); border-color: #166534; }
  100% { box-shadow: 0 0 0 4px rgba(22, 101, 52, 0);    border-color: var(--fi-line-strong, #dfe4ea); }
}
@keyframes stb-pulse-rejected {
  0%   { box-shadow: 0 0 0 4px rgba(185, 28, 28, 0.45); border-color: #b91c1c; }
  100% { box-shadow: 0 0 0 4px rgba(185, 28, 28, 0);    border-color: var(--fi-line-strong, #dfe4ea); }
}
.stb-card--landed { animation: stb-pulse-landed 1.5s ease-out 1; }
.stb-card--rejected { animation: stb-pulse-rejected 1.5s ease-out 1; }
@media (prefers-reduced-motion: reduce) {
  .stb-card--landed, .stb-card--rejected { animation-duration: 1ms; }
  .stb-card--landed { box-shadow: 0 0 0 3px rgba(22, 101, 52, 0.45); }
  .stb-card--rejected { box-shadow: 0 0 0 3px rgba(185, 28, 28, 0.45); }
}

.stb-card-top { display: flex; align-items: center; gap: 4px; flex-wrap: wrap; }
.stb-card-updated { font-size: 12px; color: var(--fi-text-muted, #5f6b78); white-space: nowrap; font-variant-numeric: tabular-nums; }
.stb-card-updated--closed { color: #166534; }
.stb-card-dates-arrow { margin: 0 2px; color: var(--fi-text-muted, #8a94a1); }
.stb-card-id { font-size: 12px; font-weight: 600; color: var(--fi-text-muted, #5f6b78); font-variant-numeric: tabular-nums; }
.stb-card-who {
  display: flex; flex-direction: column; min-width: 0;
  max-width: 60%;
  line-height: 1.25;
}
.stb-card-tenant {
  font-size: 12px; font-weight: 600;
  color: var(--fi-text-muted, #6b7280);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 100%;
}
/* the author, a step down from the account name it sits under */
.stb-card-creator {
  font-size: 11px;
  color: var(--fi-text-muted, #8a94a1);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  max-width: 100%;
}
/* the list's VIP treatment: darker gold, heavier */
.stb-card-tenant--vip { color: #9a6b00; font-weight: 700; }
.stb-card-tenant--vip .fa { font-size: 10px; }
/* the subject is the card: a half-size up and a weight up from the
   body text, in full ink */
.stb-card-subject {
  margin-top: 3px; color: var(--fi-text, #374151); line-height: 1.4;
  font-size: 14px; font-weight: 500;
  display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical;
  overflow: hidden; overflow-wrap: anywhere;
}
.stb-card-meta { display: flex; align-items: center; flex-wrap: wrap; gap: 5px 6px; margin-top: 8px; }
.stb-meta { font-size: 12px; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
.stb-meta--billable { color: #166534; font-weight: 600; }
.stb-card-foot { display: flex; align-items: center; gap: 6px; margin-top: 8px; }
.stb-flag { font-size: 12px; color: var(--fi-text-muted, #6b7280); white-space: nowrap; }
.stb-flag--stale { color: #b45309; font-weight: 600; }
.stb-flag--closed { color: #166534; }
/* the footer's right edge: hours then face, so the hours always sit
   just left of the avatar whatever else the footer holds */
.stb-card-foot-end { display: inline-flex; align-items: center; gap: 8px; flex-shrink: 0; }
.stb-card-hours { font-variant-numeric: tabular-nums; }
.stb-assignee-wrap { display: inline-flex; align-items: center; gap: 5px; }
.stb-avatar { width: 22px; height: 22px; border-radius: 50%; object-fit: cover; }
/* the viewer's own cards: a ring in the accent, the same cue as the
   selected photo chip in the filter strip */
.stb-avatar--mine { box-shadow: 0 0 0 1.5px var(--fi-surface, #fff), 0 0 0 3px var(--fi-ops-accent, #3a6e99); }
/* nobody: the struck-out head from the filter strip, at avatar size */
.stb-avatar--none {
  position: relative;
  display: inline-flex; align-items: center; justify-content: center;
  background: #eef0f2; color: #8a94a1; font-size: 12px;
}
.stb-avatar--none::after {
  content: ''; position: absolute;
  left: 3px; right: 3px; top: 50%; height: 2px; margin-top: -1px;
  background: #c0392b; transform: rotate(-45deg); border-radius: 1px;
}
/* ---- the face as the reassign control (staff) ----
   the button is invisible until it is wanted: the photo alone at rest, a
   ring and a caret under the pointer, a held ring while its picker is open */
.stb-assignee-wrap--btn {
  background: none; border: none; padding: 0; margin-left: auto;
  border-radius: 999px; cursor: pointer;
  transition: box-shadow .12s;
}
.stb-assignee-caret {
  font-size: 10px; line-height: 1;
  color: var(--fi-text-muted, #6b7280);
  opacity: 0; transition: opacity .12s;
}
.stb-assignee-wrap--btn:hover .stb-assignee-caret,
.stb-assignee-wrap--btn:focus-visible .stb-assignee-caret,
.stb-assignee-wrap--open .stb-assignee-caret { opacity: 1; }
.stb-assignee-wrap--btn:hover .stb-avatar { filter: brightness(1.08); }
.stb-assignee-wrap--open { box-shadow: 0 0 0 2px rgba(58, 110, 153, 0.55); }
.stb-assignee-wrap--open .stb-assignee-caret { transform: rotate(180deg); }
.stb-assignee-wrap--btn:focus-visible { outline: 2px solid var(--fi-ops-accent, #3a6e99); outline-offset: 2px; }
/* the write is a round trip and the face does not change until it lands */
.stb-assignee-wrap--saving { opacity: 0.5; pointer-events: none; }

/* ---- the reassign picker ----
   the vsm board's anchored-menu recipe: one popover for the whole board,
   position: fixed and placed in JS from the trigger's rect, so the lane's
   overflow scrollport cannot clip it and opening it never moves the card. */
.stb-assign-pop {
  position: fixed;
  z-index: 1080;
  display: flex; flex-direction: column; align-items: stretch; gap: 2px;
  min-width: 190px; max-width: 260px;
  background: var(--fi-surface, #fff);
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(20, 40, 60, 0.16);
  padding: 5px;
}
.stb-assign-pop-head {
  padding: 2px 8px 5px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px; text-transform: uppercase;
  color: var(--fi-text-muted, #6b7280);
  border-bottom: 1px solid var(--fi-line, #eceff3);
  margin-bottom: 3px;
}
/* one full-width row per person: their photo, their name, a check on the
   current one. comfortable row height so a mis-click lands on nobody. */
.stb-assign-opt {
  background: none; border: none; margin: 0;
  cursor: pointer; border-radius: 6px; line-height: 1;
  width: 100%; min-height: 38px; padding: 4px 8px;
  display: inline-flex; align-items: center; gap: 9px;
  font-size: 13px; color: var(--fi-text, #212529); text-align: left;
}
.stb-assign-opt:hover { background: var(--fi-hover, #f2f5f8); }
.stb-assign-opt--current { background: rgba(58, 110, 153, 0.10); font-weight: 600; }
.stb-assign-opt-name { flex: 1 1 auto; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.stb-assign-opt-check { color: var(--fi-ops-accent, #3a6e99); font-size: 12px; }
.stb-assign-opt:focus-visible { outline: 2px solid var(--fi-ops-accent, #3a6e99); outline-offset: -2px; }
@media (max-width: 640px) {
  /* 44px rows on phones -- the picker is the only way to reassign there,
     since dragging is off at these widths */
  .stb-assign-pop { min-width: 210px; max-width: calc(100vw - 24px); padding: 6px; }
  .stb-assign-opt { min-height: 44px; }
}
.stb-assignee { font-size: 12px; color: var(--fi-text-muted, #6b7280); }
.stb-assignee--none { font-style: italic; color: var(--fi-text-muted, #9aa4b1); }
.stb-you-chip {
  display: inline-block; padding: 1px 8px;
  font-size: 11px; font-weight: 700; letter-spacing: 0.3px;
  border-radius: 999px;
  background: var(--fi-ops-accent, #3a6e99); color: #fff;
}

/* status chips: one family per lane */
.stb-status-chip {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 2px 8px;
  font-size: 11px; line-height: 1.4; font-weight: 600;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 999px; border: 1px solid;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  min-width: 0;
}
.stb-status-chip .fa { font-size: 10px; line-height: 1; }
.stb-status-chip--draft     { background: #fbf7ef; color: #6b5533; border-color: #d9c4a3; border-style: dashed; }
.stb-status-chip--new       { background: #f1f3f5; color: #4b5563; border-color: #d6dde6; }
.stb-status-chip--queued    { background: #eef2f7; color: #3f4f63; border-color: #c9d3e0; }
.stb-status-chip--review    { background: #f1ebfb; color: #5b3d9e; border-color: #d8c8f2; }
.stb-status-chip--progress  { background: #e7f1ff; color: #0b3d91; border-color: #bcd8fb; }
.stb-status-chip--waiting   { background: #fff4d1; color: #7a5400; border-color: #f0d97a; }
.stb-status-chip--testing   { background: #eaedfd; color: #33408f; border-color: #c5ccf5; }
.stb-status-chip--blocked   { background: #fdeaea; color: #b91c1c; border-color: #f3c2c2; }
.stb-status-chip--completed { background: #e7f8ed; color: #166534; border-color: #b5e3c5; }
.stb-status-chip--cancelled { background: #f1f3f5; color: #6b7280; border-color: #d6dde6; }

/* type / urgency tags: the list's priority-tag palette, compact */
.stb-tag {
  display: inline-flex; align-items: center; gap: 4px;
  padding: 3px 7px;
  font-size: 10.5px; line-height: 1; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 4px; border: 1px solid;
  white-space: nowrap;
}
.stb-tag .fa { font-size: 10px; line-height: 1; }
.stb-tag-info    { background: #eaf3fb; border-color: #4a7ba7; color: #1f4a73; }
.stb-tag-warning { background: #fff4d1; border-color: #b58100; color: #7a5400; }
.stb-tag-danger  { background: #fbe3e5; border-color: #dc3545; color: #dc3545; }
.stb-tag-success { background: #edf7ee; border-color: #3a8144; color: #1f5727; }
.stb-tag-orange  { background: #ffe8d4; border-color: #ff6d00; color: #ff6d00; }
/* glyph-only tags (type, urgency, billable): a fixed square so every
   glyph, narrow or wide, takes the same box */
.stb-tag--icon, .stb-tag--billable {
  padding-left: 0; padding-right: 0;
  width: 22px; justify-content: center;
}
.stb-tag--icon .fa, .stb-tag--billable .fa { font-size: 11px; }
.stb-card--closed .stb-tag { opacity: 0.55; }

/* ---- the chip bar and the card tags under midnight ----
   the same call as support_ticket_list.vue's block: the tone families are
   pastel-fill / dark-ink pills drawn for a white page, so on the dark canvas
   an UNPRESSED chip out-shouts a pressed one and the bar reads as fully
   selected. Same five tag hues and eleven feature hues, taken as a low-alpha
   wash of the hue with its light tint as ink; the pressed state keeps its
   solid fill, which is what makes the pressed/unpressed split legible.

   the .stb-tag-* rules are shared by the filter chips and the tags on the
   cards. Both want this: .stb-card is background: var(--fi-surface), so it
   goes dark too and the pastel tag glared there just as loudly. */
/* both :not()s are load-bearing, and for the same reason: this selector would
   otherwise outrank the two rule families that are supposed to own a chip's
   border and ink. Against .stb-chip--active it would strip the pressed chip's
   #fff and accent border; against the .stb-tag-* / .stb-feat-* / .stb-flag-*
   rules below — which carry one class fewer, having no .stb-root prefix to
   ride on — it would flatten every toned chip's hue tint to this neutral
   pair, leaving the wash without the ink that names it. So this governs the
   plain chips only. --fi-line is a 0.09 white hairline, too faint to read as
   a control when a row of them sits together. */
[data-fi-theme="midnight"] .stb-chip:not(.stb-chip--active):not(.stb-chip--tone) {
  border-color: var(--fi-line-strong, rgba(255, 255, 255, 0.16));
  color: var(--fi-text, #d7e1ea);
}
/* --fi-surface-sunken is DARKER than --fi-surface under midnight, so the
   light hover rules sink a chip instead of lifting it */
[data-fi-theme="midnight"] .stb-chip:not(.stb-chip--active):not(.stb-chip--tone):hover { background: rgba(255, 255, 255, 0.07); }
[data-fi-theme="midnight"] .stb-chip--tone:hover { filter: brightness(1.22); }
[data-fi-theme="midnight"] .stb-filter-toggle:hover { background: rgba(255, 255, 255, 0.07); }
/* the pressed ring is white-on-white in light — invisible by construction —
   but the same currentColor turns into a glaring halo on dark */
[data-fi-theme="midnight"] .stb-chip.stb-chip--active {
  box-shadow: 0 0 0 2px var(--fi-surface, #182534), 0 0 0 3.5px rgba(255, 255, 255, 0.26);
}
[data-fi-theme="midnight"] .stb-group[aria-label="Status"] .stb-chip.stb-chip--active { box-shadow: none; }

[data-fi-theme="midnight"] .stb-tag-info    { background: rgba(74, 123, 167, 0.18);  border-color: rgba(74, 123, 167, 0.5);  color: #9cc4ea; }
[data-fi-theme="midnight"] .stb-tag-warning { background: rgba(181, 129, 0, 0.20);   border-color: rgba(181, 129, 0, 0.5);   color: #e5bd5c; }
[data-fi-theme="midnight"] .stb-tag-danger  { background: rgba(220, 53, 69, 0.18);   border-color: rgba(220, 53, 69, 0.5);   color: #f2949c; }
[data-fi-theme="midnight"] .stb-tag-success { background: rgba(58, 129, 68, 0.20);   border-color: rgba(58, 129, 68, 0.5);   color: #85cf94; }
[data-fi-theme="midnight"] .stb-tag-orange  { background: rgba(255, 109, 0, 0.18);   border-color: rgba(255, 109, 0, 0.5);   color: #ffab63; }
[data-fi-theme="midnight"] .stb-flag-vip      { background: rgba(181, 129, 0, 0.20); border-color: rgba(181, 129, 0, 0.5);   color: #e5bd5c; }
[data-fi-theme="midnight"] .stb-flag-billable { background: rgba(58, 129, 68, 0.20); border-color: rgba(58, 129, 68, 0.5);   color: #85cf94; }
[data-fi-theme="midnight"] .stb-flag-current  { background: rgba(58, 110, 153, 0.22); border-color: rgba(58, 110, 153, 0.55); color: #9cc4ea; }
[data-fi-theme="midnight"] .stb-feat-general          { background: rgba(138, 148, 161, 0.20); border-color: rgba(138, 148, 161, 0.5); color: #bcc5d0; }
[data-fi-theme="midnight"] .stb-feat-actuals          { background: rgba(59, 125, 221, 0.20);  border-color: rgba(59, 125, 221, 0.5);  color: #8fb8f0; }
[data-fi-theme="midnight"] .stb-feat-alerts           { background: rgba(217, 83, 79, 0.20);   border-color: rgba(217, 83, 79, 0.5);   color: #f0918e; }
[data-fi-theme="midnight"] .stb-feat-maps             { background: rgba(58, 129, 68, 0.20);   border-color: rgba(58, 129, 68, 0.5);   color: #85cf94; }
[data-fi-theme="midnight"] .stb-feat-reports          { background: rgba(122, 79, 196, 0.22);  border-color: rgba(122, 79, 196, 0.5);  color: #bda4e9; }
[data-fi-theme="midnight"] .stb-feat-vsm              { background: rgba(31, 158, 147, 0.20);  border-color: rgba(31, 158, 147, 0.5);  color: #6ed3c9; }
[data-fi-theme="midnight"] .stb-feat-data             { background: rgba(85, 96, 201, 0.22);   border-color: rgba(85, 96, 201, 0.5);   color: #a4abe9; }
[data-fi-theme="midnight"] .stb-feat-fleet-assistant  { background: rgba(194, 65, 143, 0.20);  border-color: rgba(194, 65, 143, 0.5);  color: #ea9cca; }
[data-fi-theme="midnight"] .stb-feat-driver-scorecard { background: rgba(181, 129, 0, 0.20);   border-color: rgba(181, 129, 0, 0.5);   color: #e5bd5c; }
[data-fi-theme="midnight"] .stb-feat-coaching-portal  { background: rgba(255, 109, 0, 0.18);   border-color: rgba(255, 109, 0, 0.5);   color: #ffab63; }
[data-fi-theme="midnight"] .stb-feat-erm              { background: rgba(154, 107, 62, 0.22);  border-color: rgba(154, 107, 62, 0.5);  color: #d5ab7d; }
[data-fi-theme="midnight"] .stb-feat-bus-sos          { background: rgba(192, 20, 60, 0.20);   border-color: rgba(192, 20, 60, 0.5);   color: #f0a0b2; }

/* the card foot's two status words are dark amber / dark green, picked to
   carry on a white card. #166534 in particular all but vanishes on the dark
   card, which is the one place the board hid information rather than just
   shouting it. */
[data-fi-theme="midnight"] .stb-flag--stale  { color: #e0a458; }
[data-fi-theme="midnight"] .stb-flag--closed { color: #85cf94; }

/* --fi-ops-accent (#3a6e99) is a mid-dark blue picked to carry on white; on
   the dark canvas it drops under AA, so the accent chrome moves to the
   shell's lifted link blue. the strip's 0.07 fill all but vanishes on dark. */
[data-fi-theme="midnight"] .stb-team-chip--selected {
  box-shadow: 0 0 0 1px var(--fi-surface, #182534), 0 0 0 3px var(--fi-link, #6fb1e8);
}
[data-fi-theme="midnight"] .stb-assignee-wrap--open { box-shadow: 0 0 0 2px rgba(111, 177, 232, 0.6); }
[data-fi-theme="midnight"] .stb-assign-pop {
  border-color: rgba(111, 177, 232, 0.28);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
}
[data-fi-theme="midnight"] .stb-assign-opt:hover { background: rgba(111, 177, 232, 0.12); }
[data-fi-theme="midnight"] .stb-assign-opt--current { background: rgba(111, 177, 232, 0.20); }
[data-fi-theme="midnight"] .stb-assign-opt-check { color: var(--fi-link, #6fb1e8); }
[data-fi-theme="midnight"] .stb-active {
  border-color: rgba(111, 177, 232, 0.32);
  border-left-color: var(--fi-link, #6fb1e8);
  background: rgba(111, 177, 232, 0.10);
}
[data-fi-theme="midnight"] .stb-active-label { color: var(--fi-link, #6fb1e8); }
[data-fi-theme="midnight"] .stb-active-pill { border-color: rgba(111, 177, 232, 0.5); }
[data-fi-theme="midnight"] .stb-active-pill:hover { background: rgba(111, 177, 232, 0.16); }
/* #b91c1c is a near-black red on this canvas */
[data-fi-theme="midnight"] .stb-active-pill:hover .fa { color: #f08a8a; }
[data-fi-theme="midnight"] .stb-link-btn { color: var(--fi-link, #6fb1e8); }
[data-fi-theme="midnight"] .stb-col-window-btn { color: var(--fi-link, #6fb1e8); }
[data-fi-theme="midnight"] .stb-col-window-btn:hover,
[data-fi-theme="midnight"] .stb-col-window-btn--active { background: rgba(111, 177, 232, 0.18); }
[data-fi-theme="midnight"] .stb-col-window-btn--active { color: var(--fi-text, #e6edf3); }

/* the card's status pill. its own palette, not the tone families above --
   nine pastels on a white card, of which --new / --queued / --cancelled are
   barely tinted at all and read as plain white blocks on the dark card. same
   nine hues as low-alpha washes with a light tint for ink; cancelled stays
   the quietest of them, as it is in light. */
[data-fi-theme="midnight"] .stb-status-chip--new       { background: rgba(138, 148, 161, 0.20); color: #bcc5d0; border-color: rgba(138, 148, 161, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--queued    { background: rgba(99, 124, 158, 0.22);  color: #b0c2d8; border-color: rgba(99, 124, 158, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--review    { background: rgba(122, 79, 196, 0.22);  color: #bda4e9; border-color: rgba(122, 79, 196, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--progress  { background: rgba(59, 125, 221, 0.22);  color: #8fb8f0; border-color: rgba(59, 125, 221, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--waiting   { background: rgba(181, 129, 0, 0.20);   color: #e5bd5c; border-color: rgba(181, 129, 0, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--testing   { background: rgba(85, 96, 201, 0.22);   color: #a4abe9; border-color: rgba(85, 96, 201, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--blocked   { background: rgba(220, 53, 69, 0.18);   color: #f2949c; border-color: rgba(220, 53, 69, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--completed { background: rgba(58, 129, 68, 0.20);   color: #85cf94; border-color: rgba(58, 129, 68, 0.45); }
[data-fi-theme="midnight"] .stb-status-chip--cancelled { background: rgba(138, 148, 161, 0.13); color: #9aa6b3; border-color: rgba(138, 148, 161, 0.35); }

/* the card's feature chip, when no .stb-feat-* hue lands on it (an unmapped
   or blank category): the toned rules above cover every known feature, this
   is the fallback they sit on. */
[data-fi-theme="midnight"] .stb-cat-chip {
  background: rgba(138, 148, 161, 0.16);
  color: #bcc5d0;
  border-color: rgba(138, 148, 161, 0.4);
}
/* the struck-out head standing in for "nobody": a light disc in the team
   strip, the one bright spot left in the scope row */
[data-fi-theme="midnight"] .stb-team-chip-icon {
  background: rgba(255, 255, 255, 0.10);
  color: var(--fi-text-muted, #93a5b7);
}

/* the same box as .stb-tag (padding, type, line-height) so the feature
   chip sits level with the type and urgency tags beside it */
.stb-cat-chip {
  display: inline-flex; align-items: center;
  padding: 3px 7px;
  font-size: 10.5px; line-height: 1; font-weight: 700;
  text-transform: uppercase; letter-spacing: 0.3px;
  border-radius: 4px;
  background: #eef1f5; color: #4b5563; border: 1px solid #e2e7ec;
  white-space: nowrap;
}
/* feature tones: one hue per category, shared by the filter chip and the
   card's feature chip so a colour means the same thing in both places.
   the pressed chip fills with its border colour (stb-chip--tone recipe). */
.stb-feat-general          { background: #eef1f5; border-color: #8a94a1; color: #4b5563; }
.stb-feat-actuals          { background: #e7f1ff; border-color: #3b7ddd; color: #0b3d91; }
.stb-feat-alerts           { background: #fdeaea; border-color: #d9534f; color: #a12622; }
.stb-feat-maps             { background: #e7f8ed; border-color: #3a8144; color: #1f5727; }
.stb-feat-reports          { background: #f1ebfb; border-color: #7a4fc4; color: #4c2a8a; }
.stb-feat-vsm              { background: #e3f6f4; border-color: #1f9e93; color: #0f5f58; }
.stb-feat-data             { background: #eaedfd; border-color: #5560c9; color: #33408f; }
.stb-feat-fleet-assistant  { background: #fbe7f4; border-color: #c2418f; color: #8a2b64; }
.stb-feat-driver-scorecard { background: #fff4d1; border-color: #b58100; color: #7a5400; }
.stb-feat-coaching-portal  { background: #ffe8d4; border-color: #ff6d00; color: #b34c00; }
.stb-feat-erm              { background: #f3ede6; border-color: #9a6b3e; color: #6b4423; }
.stb-feat-bus-sos          { background: #fde5ea; border-color: #c0143c; color: #8a0d2b; }
.stb-flag-vip      { background: #fff4d1; border-color: #b58100; color: #9a6b00; }
.stb-flag-billable { background: #e7f8ed; border-color: #3a8144; color: #166534; }
.stb-flag-current  { background: #e9f1f8; border-color: #3a6e99; color: #2a5678; }
.stb-chip--tone.stb-chip--active.stb-flag-vip      { background: #b58100; border-color: #b58100; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-flag-billable { background: #3a8144; border-color: #3a8144; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-flag-current  { background: #3a6e99; border-color: #3a6e99; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-general          { background: #8a94a1; border-color: #8a94a1; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-actuals          { background: #3b7ddd; border-color: #3b7ddd; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-alerts           { background: #d9534f; border-color: #d9534f; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-maps             { background: #3a8144; border-color: #3a8144; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-reports          { background: #7a4fc4; border-color: #7a4fc4; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-vsm              { background: #1f9e93; border-color: #1f9e93; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-data             { background: #5560c9; border-color: #5560c9; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-fleet-assistant  { background: #c2418f; border-color: #c2418f; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-driver-scorecard { background: #b58100; border-color: #b58100; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-coaching-portal  { background: #ff6d00; border-color: #ff6d00; color: #fff; }
.stb-chip--tone.stb-chip--active.stb-feat-erm              { background: #9a6b3e; border-color: #9a6b3e; color: #fff; }

@media (max-width: 768px) {
  .stb-root { padding: 12px 8px 2px 8px; }
  /* dragging is disabled at this width (see isMobileView), so the card is
     a tap target that opens the ticket */
  .stb-card { cursor: pointer; }
  .stb-search { min-width: 100%; margin-left: 0; }
  .stb-scope-selects { margin-left: 0; }
}

@media (max-width: 600px) {
  .stb-btn-label { display: none; }
  .stb-mobile-only { display: inline-flex; }
  .stb-root ::v-deep .fi-pr-rail { flex-wrap: nowrap; }
  .stb-root ::v-deep .fi-pr-actions { width: auto; flex-shrink: 0; }
  .stb-root ::v-deep .fi-pr-title { min-width: 0; overflow: hidden; }
  .stb-filters-collapsed .stb-scope-row,
  .stb-filters-collapsed .stb-filter-row,
  .stb-filters-collapsed .stb-active { display: none; }
  .stb-filter-row { flex-direction: column; align-items: flex-start; gap: 8px; }
  .stb-group { flex-wrap: wrap; }
  .stb-tenant-select, .stb-scope-select { max-width: 100%; }

  /* one lane per screen; the next lane's edge stays visible as the hint
     that there are more, and the scroll snaps lane to lane */
  .stb-board {
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
  }
  .stb-col {
    flex: 0 0 86%;
    min-width: 0;
    scroll-snap-align: start;
  }
  .stb-col--collapsed { flex: 0 0 38px; }
}

/* ---- invoice prep modal ---- */
.stb-billable-bar { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; }
.stb-billable-month,
.stb-billable-assignee { width: auto; max-width: 220px; }
.stb-billable-text {
  margin: 0;
  padding: 10px 12px;
  max-height: 60vh;
  overflow: auto;
  font-size: 12px;
  line-height: 1.5;
  white-space: pre;
  color: var(--fi-text, #374151);
  background: var(--fi-surface-sunken, #f6f8fb);
  border: 1px solid var(--fi-line-strong, #d6dde6);
  border-radius: 6px;
}
.stb-billable-warn { font-size: 0.85em; color: #92400e; margin-bottom: 8px; }
.stb-billable-empty { margin: 0; }
</style>
